import { memo, startTransition, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type ChangeEvent, type SetStateAction } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { ScrollArea } from './ui/scroll-area';
import { Skeleton } from './ui/skeleton';
import { Switch } from './ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { apiRequest, downloadBinary } from '../lib/api';
import { openOrSaveBlobOnDevice } from '../lib/nativeFileAccess';
import { navigateToExamSameTab } from '../lib/examWindowLaunch';
import { getMediaUrl } from '../lib/publicMedia';
import { bearerForLaunchUrl } from '../lib/authSession';
import { App as CapacitorApp } from '@capacitor/app';
import { isNativeRuntime, logNativeEvent } from '../lib/nativeDiagnostics';
import { formatDemoTimeLeft, useDemoMode } from '../lib/demoMode';
import { DemoModeBanner } from './subscription/DemoModeButton';
import { useNavigate } from 'react-router-dom';
import { consumeBriefNativeHide, markNativeDocumentHidden } from '../lib/nativeForeground';
import {
  acquireRealtimeSocket,
  isRealtimeConnected,
  reconnectRealtimeIfNeeded,
  releaseRealtimeSocket,
  setRealtimeAuthToken,
  subscribeRealtimeStatus,
} from '../lib/realtimeSocket';
import { useAuth } from '../context/AuthContext';
import { useSubscription } from '../context/SubscriptionContext';
import { PremiumLockScreen } from './subscription/PremiumLockScreen';
import { PremiumCountdownBadge } from './subscription/PremiumCountdownBadge';
import { showSuccessToast, showErrorToast, showInfoToast, showWarningToast, showNeutralToast, handleApiError, audienceFriendlyError } from '../lib/userToast';

interface CommunityUser {
  id: string;
  userId?: string;
  firstName?: string;
  lastName?: string;
  targetProgram?: string;
  city?: string;
  score?: number;
  weakTopics?: string[];
  username?: string;
  shareProfilePicture?: boolean;
  profilePictureUrl?: string;
  favoriteSubjects?: string[];
  targetNetType?: string;
  subjectsNeedHelp?: string[];
  preparationLevel?: 'beginner' | 'intermediate' | 'advanced';
  studyTimePreference?: 'morning' | 'evening' | 'night' | 'flexible';
  testScoreRange?: { min: number; max: number };
  bio?: string;
  connectionStatus?: 'none' | 'connected' | 'pending-sent' | 'pending-received' | string;
  hideOnlineStatus?: boolean;
  doNotDisturb?: boolean;
  lastSeenAt?: string | null;
  communityInterests?: string[];
  presenceStatus?: 'online' | 'away' | 'offline';
  studyingSubject?: string;
  activity?: string;
}

interface OnlineStudentRow extends CommunityUser {
  presenceStatus?: 'online' | 'away' | 'offline';
  studyingSubject?: string;
  activity?: string;
}

function addPresenceId(keys: Set<string>, value: unknown) {
  const id = String(value || '').trim();
  if (!id) return;
  keys.add(`id:${id}`);
  keys.add(`id:${id.toLowerCase()}`);
}

function addPresenceUsername(keys: Set<string>, value: unknown) {
  const username = String(value || '').trim().toLowerCase();
  if (!username) return;
  keys.add(`user:${username}`);
}

/** Backend user id, community profile id, and unique username — never display name. */
function buildSelfPresenceKeys(
  userId: unknown,
  profileId: unknown,
  profileUserId: unknown,
  username: unknown,
): Set<string> {
  const keys = new Set<string>();
  addPresenceId(keys, userId);
  addPresenceId(keys, profileId);
  addPresenceId(keys, profileUserId);
  addPresenceUsername(keys, username);
  return keys;
}

function isSelfOnlineStudent(
  row: { id?: string; userId?: string; username?: string } | null | undefined,
  selfKeys: Set<string>,
): boolean {
  if (!row || selfKeys.size === 0) return false;
  const id = String(row.id || '').trim();
  const userId = String(row.userId || '').trim();
  const username = String(row.username || '').trim().toLowerCase();
  if (id && (selfKeys.has(`id:${id}`) || selfKeys.has(`id:${id.toLowerCase()}`))) return true;
  if (userId && (selfKeys.has(`id:${userId}`) || selfKeys.has(`id:${userId.toLowerCase()}`))) return true;
  if (username && selfKeys.has(`user:${username}`)) return true;
  return false;
}

function withoutSelfOnlineStudents<T extends { id?: string; userId?: string; username?: string }>(
  rows: T[],
  selfKeys: Set<string>,
): T[] {
  if (!selfKeys.size || rows.length === 0) return rows;
  let removed = false;
  const next: T[] = [];
  for (const row of rows) {
    if (isSelfOnlineStudent(row, selfKeys)) {
      removed = true;
      continue;
    }
    next.push(row);
  }
  return removed ? next : rows;
}

interface CommunityRequestRow {
  id: string;
  direction: 'incoming' | 'outgoing';
  status: string;
  createdAt: string | null;
  user: CommunityUser;
}

interface ConnectionRow {
  connectionId: string;
  connectedAt: string | null;
  user: CommunityUser;
  unreadCount: number;
  blockedByMe?: boolean;
  blockedByOther?: boolean;
  canMessage?: boolean;
}

interface MessageAttachment {
  name: string;
  mimeType: string;
  size: number;
  dataUrl: string;
}

interface MessageReaction {
  userId?: string;
  emoji: string;
  reactedAt: string | null;
}

interface MessageRow {
  id: string;
  connectionId: string;
  senderUserId: string;
  messageType?: 'text' | 'file' | 'voice' | 'call-invite' | string;
  text: string;
  attachment?: MessageAttachment | null;
  voiceMeta?: { durationSeconds: number } | null;
  callInvite?: { mode: 'audio' | 'video' | string; roomUrl: string; roomCode?: string } | null;
  reactions?: MessageReaction[];
  createdAt: string | null;
  readByUserIds?: string[];
}

interface DiscussionRoom {
  id: string;
  title: string;
  subject: string;
  posts: number;
}

interface DiscussionPost {
  id: string;
  roomId: string;
  type: 'discussion' | 'doubt';
  title: string;
  text: string;
  subject: string;
  upvotes: number;
  createdAt: string | null;
  author: CommunityUser | null;
  answers: Array<{
    id: string;
    text: string;
    upvotes: number;
    createdAt: string | null;
    author: CommunityUser | null;
  }>;
}

interface BadgeRow {
  id: string;
  label: string;
  icon: string;
  description?: string;
  earned: boolean;
  progress: number;
  target: number;
  unlockedAt?: string | null;
}

const BADGE_ICON_BY_ID: Record<string, string> = {
  'practice-master': '\u{1F4DA}',
  'accuracy-king': '\u{1F3AF}',
  'physics-expert': '\u{1F9E0}',
  'study-streak-7': '\u{1F525}',
  'leaderboard-top10': '\u{1F3C6}',
  'doubt-contributor': '\u{1F3C5}',
};

function badgeIcon(badge: BadgeRow) {
  return BADGE_ICON_BY_ID[badge.id] || (badge.icon && !badge.icon.includes('\u00F0') ? badge.icon : '\u{1F3C6}');
}

function formatBadgeUnlockDate(value?: string | null) {
  if (!value) return '';
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return '';
  return date.toLocaleDateString(undefined, { day: 'numeric', month: 'long', year: 'numeric' });
}

interface LeaderboardRow extends CommunityUser {
  rank: number;
  averageScore: number;
  tests: number;
  accuracy: number;
  improvement: number;
}

interface QuizChallengeQuestion {
  questionId: string;
  subject: string;
  topic: string;
  question: string;
  options: string[];
  difficulty: string;
  correctAnswer?: string;
}

interface QuizChallengeResult {
  submitted: boolean;
  completedAt: string | null;
  elapsedSeconds: number;
  answers?: Array<{ questionId: string; selectedOption: string }>;
  correctCount: number;
  wrongCount: number;
  unansweredCount: number;
  accuracyScore: number;
  speedScore: number;
  totalScore: number;
}

interface QuizLiveProgress {
  answeredCount: number;
  correctCount: number;
  elapsedSeconds: number;
  updatedAt: string | null;
}

interface QuizChallengeRow {
  id: string;
  challengerUserId: string;
  opponentUserId: string;
  mode: 'subject-wise' | 'mock' | 'adaptive' | 'custom' | string;
  challengeType?: 'async' | 'live' | string;
  subject: string;
  topic: string;
  difficulty: string;
  questionCount: number;
  durationSeconds: number;
  status: 'pending' | 'accepted' | 'in_progress' | 'completed' | 'declined' | 'cancelled' | 'expired' | string;
  invitedAt: string | null;
  acceptedAt: string | null;
  acceptedDeadlineAt?: string | null;
  startedAt: string | null;
  endedAt: string | null;
  winnerUserId: string;
  isChallenger: boolean;
  myResult: QuizChallengeResult;
  opponentResult: QuizChallengeResult;
  myLiveProgress?: QuizLiveProgress;
  opponentLiveProgress?: QuizLiveProgress;
  questions: QuizChallengeQuestion[];
}

interface QuizLeaderboardRow {
  rank: number;
  userId: string;
  username?: string;
  name?: string;
  avatar?: string | null;
  totalWins: number;
  totalMatchesPlayed: number;
  winRate: number;
  totalChallengesSent: number;
  totalChallengesAccepted: number;
}

const PROFILE_PICTURE_ALLOWED_MIME_TYPES = new Set([
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/gif',
  'image/svg+xml',
]);
const PROFILE_PICTURE_MAX_BYTES = 3 * 1024 * 1024;
const CHAT_ATTACHMENT_MAX_BYTES = 10 * 1024 * 1024;
const COMMUNITY_FILE_ALLOWED_MIME_TYPES = new Set([
  'application/pdf',
  'application/x-pdf',
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/webp',
]);
const COMMUNITY_FILE_ALLOWED_EXTENSIONS = new Set(['.pdf', '.jpg', '.jpeg', '.png', '.webp']);
const QUICK_CHAT_EMOJIS = ['😀', '😂', '🔥', '👏', '❤️', '👍'];
const ENCRYPTION_LABEL = 'Messages are end-to-end encrypted.';
const COMMUNITY_CACHE_PREFIX = 'net360:community-cache:';
const COMMUNITY_CACHE_TTL_MS = 45_000;

type CommunityCacheEntry = {
  expiresAt: number;
  payload: unknown;
};

function readExpiredCommunityCachePayload<T>(path: string): T | null {
  const now = Date.now();
  try {
    const raw = localStorage.getItem(`${COMMUNITY_CACHE_PREFIX}${path}`);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CommunityCacheEntry;
    if (!parsed || typeof parsed.expiresAt !== 'number' || parsed.expiresAt <= now) {
      return null;
    }
    return parsed.payload as T;
  } catch {
    return null;
  }
}

const CHAT_ATTACHMENT_ACCEPT = [
  '.pdf',
  '.jpg',
  '.jpeg',
  '.png',
  '.webp',
  'application/pdf',
  'image/jpeg',
  'image/png',
  'image/webp',
].join(',');

function communityFileExtension(name: string) {
  const raw = String(name || '').trim().toLowerCase();
  const dot = raw.lastIndexOf('.');
  return dot >= 0 ? raw.slice(dot) : '';
}

function isAllowedCommunityChatFile(file: File) {
  const mime = String(file.type || '').toLowerCase();
  const extension = communityFileExtension(file.name);
  if (COMMUNITY_FILE_ALLOWED_MIME_TYPES.has(mime)) return true;
  if (!mime && COMMUNITY_FILE_ALLOWED_EXTENSIONS.has(extension)) return true;
  return false;
}

function isSafeCommunityImageMime(mimeType: string) {
  return ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'].includes(String(mimeType || '').toLowerCase());
}

function isSafeCommunityPdfMime(mimeType: string) {
  const mime = String(mimeType || '').toLowerCase();
  return mime === 'application/pdf' || mime === 'application/x-pdf';
}

function isSafeCommunityImageDataUrl(dataUrl: string) {
  return /^data:image\/(jpeg|jpg|png|webp);/i.test(String(dataUrl || ''));
}

function fileToDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ''));
    reader.onerror = () => reject(new Error('Could not read selected image file.'));
    reader.readAsDataURL(file);
  });
}

function createCallRoomUrl(connectionId: string) {
  const roomCode = `net360-${connectionId}-${Date.now()}`;
  return {
    roomCode,
    roomUrl: `https://meet.jit.si/${roomCode}`,
  };
}

/** Map Tailwind spacing scale (e.g. h-8, w-12) to pixels for stable <img> dimensions (w-1 = 4px). */
function dimensionsFromSizeClass(sizeClass: string): { width: number; height: number } {
  const toPx = (token: string) => {
    const n = Number(token);
    return Number.isFinite(n) ? Math.round(n * 4) : 32;
  };
  const hMatch = sizeClass.match(/\bh-(\d+(?:\.\d+)?)\b/);
  const wMatch = sizeClass.match(/\bw-(\d+(?:\.\d+)?)\b/);
  const h = hMatch ? toPx(hMatch[1]!) : null;
  const w = wMatch ? toPx(wMatch[1]!) : null;
  const side = h ?? w ?? 32;
  return { width: w ?? side, height: h ?? side };
}

function getAvatarFallback(userLike: { firstName?: string; lastName?: string; username?: string }) {
  const first = String(userLike.firstName || '').trim();
  const last = String(userLike.lastName || '').trim();
  const uname = String(userLike.username || '').trim();
  const label = [first, last].filter(Boolean).join(' ') || uname || 'U';
  const parts = label.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  }
  return label.slice(0, 2).toUpperCase();
}

const CommunityAvatar = memo(function CommunityAvatar({
  userLike,
  sizeClass = 'h-8 w-8',
}: {
  userLike: { firstName?: string; lastName?: string; username?: string; profilePictureUrl?: string };
  sizeClass?: string;
}) {
  const image = String(userLike.profilePictureUrl || '').trim();
  const fallback = getAvatarFallback(userLike);
  const { width, height } = useMemo(() => dimensionsFromSizeClass(sizeClass), [sizeClass]);

  if (image) {
    return (
      <img
        src={getMediaUrl(image)}
        alt={fallback}
        width={width}
        height={height}
        className={`${sizeClass} rounded-full border object-cover`}
        loading="lazy"
        decoding="async"
      />
    );
  }

  return (
    <div className={`${sizeClass} grid place-items-center rounded-full border bg-slate-100 text-[11px] font-medium text-slate-600`}>
      {fallback}
    </div>
  );
});

const SafeChatAttachment = memo(function SafeChatAttachment({
  messageId,
  attachment,
  token,
}: {
  messageId: string;
  attachment: MessageAttachment;
  token: string | null;
}) {
  const [imageUrl, setImageUrl] = useState('');
  const mimeType = String(attachment.mimeType || '').toLowerCase();
  const isImage = isSafeCommunityImageMime(mimeType);
  const isPdf = isSafeCommunityPdfMime(mimeType);
  const localImageUrl = isImage && isSafeCommunityImageDataUrl(attachment.dataUrl) ? attachment.dataUrl : '';

  useEffect(() => {
    if (!isImage || localImageUrl || !token || !messageId) {
      setImageUrl('');
      return;
    }
    let objectUrl = '';
    let cancelled = false;
    void (async () => {
      try {
        const { blob } = await downloadBinary(`/api/community/messages/${encodeURIComponent(messageId)}/attachment`, {}, token);
        if (cancelled) return;
        if (!blob.type.startsWith('image/')) return;
        objectUrl = URL.createObjectURL(blob);
        setImageUrl(objectUrl);
      } catch {
        if (!cancelled) setImageUrl('');
      }
    })();
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [isImage, localImageUrl, messageId, token]);

  const openSecureAttachment = async (download: boolean) => {
    if (!token || !messageId) {
      showErrorToast('Sign in again to open this file.');
      return;
    }
    try {
      const query = download ? '?download=1' : '';
      const { blob, filename } = await downloadBinary(
        `/api/community/messages/${encodeURIComponent(messageId)}/attachment${query}`,
        {},
        token,
      );
      await openOrSaveBlobOnDevice(
        blob,
        filename || attachment.name || 'community-file',
        download ? 'download' : 'open',
      );
    } catch {
      showErrorToast('Could not open this file securely.');
    }
  };

  return (
    <div className="space-y-2">
      <p>{attachment.name || 'Shared a file'}</p>
      {isImage && (localImageUrl || imageUrl) ? (
        <img
          src={localImageUrl || imageUrl}
          alt={attachment.name || 'Shared image'}
          className="max-h-56 max-w-full rounded-lg object-contain"
        />
      ) : null}
      <div className="flex flex-wrap gap-2">
        {isPdf ? (
          <button
            type="button"
            className="text-xs underline underline-offset-2"
            onClick={() => void openSecureAttachment(false)}
          >
            Open PDF
          </button>
        ) : null}
        <button
          type="button"
          className="text-xs underline underline-offset-2"
          onClick={() => void openSecureAttachment(true)}
        >
          {isPdf ? 'Download PDF' : isImage ? 'Download image' : 'Download file'}
        </button>
      </div>
    </div>
  );
});

function displayName(user: CommunityUser) {
  const full = [user.firstName, user.lastName].filter(Boolean).join(' ').trim();
  return full || user.username || 'Student';
}

function onlineRowFromPresenceEvent(uid: string, parsed: {
  status?: string;
  activity?: string;
  studyingSubject?: string;
  displayName?: string;
  username?: string;
  lastSeenAt?: string;
}): OnlineStudentRow {
  const parts = String(parsed.displayName || '').trim().split(/\s+/).filter(Boolean);
  return {
    id: uid,
    userId: uid,
    firstName: parts[0] || '',
    lastName: parts.slice(1).join(' '),
    username: String(parsed.username || ''),
    presenceStatus: parsed.status === 'away' ? 'away' : 'online',
    activity: parsed.activity !== undefined ? String(parsed.activity) : '',
    studyingSubject: parsed.studyingSubject !== undefined ? String(parsed.studyingSubject) : '',
    lastSeenAt: parsed.lastSeenAt || null,
    connectionStatus: 'none',
  };
}

function canSendConnectionRequest(status?: string) {
  return !status || status === 'none';
}

type RosterActionKind = 'chat' | 'quiz' | 'room';

/** Tab each roster action lands on; the pressed state clears once that tab is actually active. */
const ROSTER_ACTION_TARGET_TAB: Record<RosterActionKind, string> = {
  chat: 'messages',
  quiz: 'quiz-battles',
  room: 'discussion-rooms',
};

const PRESENCE_ROW_COMPARE_KEYS: Array<keyof OnlineStudentRow> = [
  'presenceStatus',
  'studyingSubject',
  'activity',
  'connectionStatus',
  'lastSeenAt',
  'doNotDisturb',
  'hideOnlineStatus',
  'firstName',
  'lastName',
  'username',
  'profilePictureUrl',
  'targetNetType',
];

function presenceRowEquals(a: OnlineStudentRow, b: OnlineStudentRow) {
  return PRESENCE_ROW_COMPARE_KEYS.every((key) => a[key] === b[key]);
}

/**
 * Merge an authoritative server snapshot into the current roster: dedupe by stable user id,
 * keep the server's order, and reuse row objects whose visible fields did not change so React
 * only re-renders the affected tiles. Returns `prev` untouched when nothing changed.
 */
function mergePresenceRoster(prev: OnlineStudentRow[], next: OnlineStudentRow[]): OnlineStudentRow[] {
  const prevById = new Map(prev.map((row) => [String(row.id), row]));
  const seen = new Set<string>();
  const merged: OnlineStudentRow[] = [];
  let changed = false;

  for (const incoming of next) {
    const id = String(incoming?.id || '').trim();
    if (!id || seen.has(id)) continue;
    seen.add(id);
    const existing = prevById.get(id);
    if (existing && presenceRowEquals(existing, incoming)) {
      merged.push(existing);
    } else {
      merged.push({ ...incoming, id });
      changed = true;
    }
  }

  if (!changed && merged.length === prev.length && merged.every((row, index) => row === prev[index])) {
    return prev;
  }
  return merged;
}

function upsertCommunityMessage(list: MessageRow[], incoming: MessageRow): MessageRow[] {
  const id = String(incoming?.id || '');
  if (!id) return list;
  const index = list.findIndex((row) => String(row.id) === id);
  if (index === -1) return [...list, incoming];
  const next = list.slice();
  next[index] = { ...list[index], ...incoming, id };
  return next;
}

function CommunityInner() {
  const { token, user } = useAuth();
  const { surface, me, loading: subLoading } = useSubscription();
  const communityPaidAllowed = Boolean(me?.paidServices?.community?.allowed);
  const demo = useDemoMode();
  const demoCommunity = !communityPaidAllowed && demo.communityActive;
  const demoCommunityExpired = !communityPaidAllowed && demo.communityExpired;
  const communityAccessAllowed = communityPaidAllowed || demoCommunity;
  const navigate = useNavigate();

  useEffect(() => {
    if (!demoCommunityExpired || subLoading) return;
    showInfoToast('Your 24-hour demo Community access has ended. Subscribe to Community to continue.');
    navigate('/subscription', { replace: true });
  }, [demoCommunityExpired, navigate, subLoading]);

  const [searchLoading, setSearchLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [discoverSearched, setDiscoverSearched] = useState(false);
  const [activeTab, setActiveTab] = useState('discover-students');
  const [leaderboardPeriod, setLeaderboardPeriod] = useState<'weekly' | 'monthly'>('weekly');

  useEffect(() => {
    const activity = activeTab === 'quiz-battles'
      ? 'Quiz Battle'
      : activeTab === 'discussion-rooms'
        ? 'Study Room'
        : 'Community';
    window.dispatchEvent(new CustomEvent('net360:presence-activity', { detail: { activity } }));
    return () => {
      window.dispatchEvent(new CustomEvent('net360:presence-activity', { detail: { activity: '' } }));
    };
  }, [activeTab]);

  const [profile, setProfile] = useState<CommunityUser | null>(null);
  const [usernameInput, setUsernameInput] = useState('');
  const [profilePictureDataUrl, setProfilePictureDataUrl] = useState('');
  const [profilePictureUploadName, setProfilePictureUploadName] = useState('');
  const [shareProfilePicture, setShareProfilePicture] = useState(false);
  const [targetNetType, setTargetNetType] = useState('net-engineering');
  const [subjectsNeedHelpInput, setSubjectsNeedHelpInput] = useState('');
  const [preparationLevel, setPreparationLevel] = useState<'beginner' | 'intermediate' | 'advanced'>('intermediate');
  const [studyTimePreference, setStudyTimePreference] = useState<'morning' | 'evening' | 'night' | 'flexible'>('flexible');
  const [scoreRangeMin, setScoreRangeMin] = useState(0);
  const [scoreRangeMax, setScoreRangeMax] = useState(200);
  const [bio, setBio] = useState('');
  const [isCommunityProfileExpanded, setIsCommunityProfileExpanded] = useState(true);
  const [hideOnlineStatus, setHideOnlineStatus] = useState(false);
  const [doNotDisturb, setDoNotDisturb] = useState(false);
  const [interestsInput, setInterestsInput] = useState('');
  const [onlineStudents, setOnlineStudentsState] = useState<OnlineStudentRow[]>([]);
  const selfPresenceKeys = useMemo(
    () => buildSelfPresenceKeys(user?.id, profile?.id, profile?.userId, profile?.username),
    [user?.id, profile?.id, profile?.userId, profile?.username],
  );
  const selfPresenceKeysRef = useRef(selfPresenceKeys);
  selfPresenceKeysRef.current = selfPresenceKeys;
  const setOnlineStudents = useCallback((action: SetStateAction<OnlineStudentRow[]>) => {
    setOnlineStudentsState((prev) => {
      const next = typeof action === 'function' ? action(prev) : action;
      return withoutSelfOnlineStudents(next, selfPresenceKeysRef.current);
    });
  }, []);
  const otherOnlineStudents = useMemo(
    () => withoutSelfOnlineStudents(onlineStudents, selfPresenceKeys),
    [onlineStudents, selfPresenceKeys],
  );
  useEffect(() => {
    setOnlineStudentsState((prev) => withoutSelfOnlineStudents(prev, selfPresenceKeys));
  }, [selfPresenceKeys]);
  const [presenceLoading, setPresenceLoading] = useState(false);
  const [presenceResolved, setPresenceResolved] = useState(false);
  const [remoteTyping, setRemoteTyping] = useState(false);
  const [studyingSubjectPing] = useState('');
  /** True while the shared realtime socket is not connected (drives the "Reconnecting…" badge + faster fallback polling). */
  const [realtimeDown, setRealtimeDown] = useState(() => !isRealtimeConnected('student'));

  const messageThreadEndRef = useRef<HTMLDivElement | null>(null);
  const activeConnectionIdRef = useRef('');
  const typingEmitTimerRef = useRef<number | null>(null);
  const typingStopTimerRef = useRef<number | null>(null);
  const typingIdleTimerRef = useRef<number | null>(null);
  const lastSendFingerprintRef = useRef('');
  const loadPresenceRef = useRef<(options?: { silent?: boolean }) => Promise<void>>(async () => {});
  const presenceRequestSeqRef = useRef(0);
  const presenceReconcileTimerRef = useRef<number | null>(null);
  const pingPresenceRef = useRef<() => void>(() => {});
  const [connectingUserIds, setConnectingUserIds] = useState<ReadonlySet<string>>(() => new Set());
  const [pendingRosterAction, setPendingRosterAction] = useState<{ userId: string; kind: RosterActionKind } | null>(null);
  const [searchResults, setSearchResults] = useState<Array<CommunityUser & { connectionStatus?: string }>>([]);
  const [profilePreview, setProfilePreview] = useState<CommunityUser | null>(null);
  const [studyPartnersProfilePreview, setStudyPartnersProfilePreview] = useState<CommunityUser | null>(null);

  const [incomingRequests, setIncomingRequests] = useState<CommunityRequestRow[]>([]);
  const [outgoingRequests, setOutgoingRequests] = useState<CommunityRequestRow[]>([]);

  const [studyPartners, setStudyPartners] = useState<Array<{ compatibility: number; user: CommunityUser; reasons?: string[] }>>([]);

  const [rooms, setRooms] = useState<DiscussionRoom[]>([]);
  const [activeRoomId, setActiveRoomId] = useState('');
  const [roomPosts, setRoomPosts] = useState<DiscussionPost[]>([]);
  const [feedPosts, setFeedPosts] = useState<DiscussionPost[]>([]);
  const [openThreadId, setOpenThreadId] = useState('');
  const [newPostType, setNewPostType] = useState<'discussion' | 'doubt'>('discussion');
  const [newPostTitle, setNewPostTitle] = useState('');
  const [newPostText, setNewPostText] = useState('');
  const [answerTextByPostId, setAnswerTextByPostId] = useState<Record<string, string>>({});

  const [leaderboard, setLeaderboard] = useState<LeaderboardRow[]>([]);
  const [badges, setBadges] = useState<BadgeRow[]>([]);
  const [selectedBadge, setSelectedBadge] = useState<BadgeRow | null>(null);
  const [isDownloadingCertificate, setIsDownloadingCertificate] = useState(false);

  const [quizChallenges, setQuizChallenges] = useState<QuizChallengeRow[]>([]);
  const [quizLeaderboard, setQuizLeaderboard] = useState<QuizLeaderboardRow[]>([]);

  const competitiveLeaderboardScrollRef = useRef<HTMLDivElement>(null);
  const quizLeaderboardScrollRef = useRef<HTMLDivElement>(null);

  const competitiveLeaderboardVirtual = useVirtualizer({
    count: leaderboard.length,
    getScrollElement: () => competitiveLeaderboardScrollRef.current,
    estimateSize: () => 84,
    overscan: 8,
  });

  const quizLeaderboardVirtual = useVirtualizer({
    count: quizLeaderboard.length,
    getScrollElement: () => quizLeaderboardScrollRef.current,
    estimateSize: () => 76,
    overscan: 6,
  });
  const [selectedQuizChallengeId, setSelectedQuizChallengeId] = useState('');
  const [quizMode, setQuizMode] = useState<'subject-wise' | 'mock' | 'adaptive' | 'custom'>('subject-wise');
  const [quizSubject, setQuizSubject] = useState('mathematics');
  const [quizTopic, setQuizTopic] = useState('');
  const [quizDifficulty, setQuizDifficulty] = useState('Medium');
  const [quizChallengeType, setQuizChallengeType] = useState<'async' | 'live'>('async');
  const [quizQuestionCount, setQuizQuestionCount] = useState(15);
  const [quizDurationSeconds, setQuizDurationSeconds] = useState(900);
  const [quizOpponentUserId, setQuizOpponentUserId] = useState('');
  const [allCommunityUsers, setAllCommunityUsers] = useState<Array<CommunityUser & { connectionStatus?: string }>>([]);
  const [quizAnswers, setQuizAnswers] = useState<Record<string, string>>({});
  const [quizStartedAtMs, setQuizStartedAtMs] = useState<number | null>(null);

  const [connections, setConnections] = useState<ConnectionRow[]>([]);
  const [activeConnectionId, setActiveConnectionId] = useState('');
  const [messages, setMessages] = useState<MessageRow[]>([]);
  const [messagesThreadLoading, setMessagesThreadLoading] = useState(false);
  const [messageInput, setMessageInput] = useState('');
  const [messageAttachment, setMessageAttachment] = useState<MessageAttachment | null>(null);
  const [isSendingMessage, setIsSendingMessage] = useState(false);
  const [isBlockingConnection, setIsBlockingConnection] = useState(false);
  const [isUnfriendingConnection, setIsUnfriendingConnection] = useState(false);
  const [isRecordingVoice, setIsRecordingVoice] = useState(false);
  const [isSavingCommunityProfile, setIsSavingCommunityProfile] = useState(false);
  const [isPostingRoom, setIsPostingRoom] = useState(false);
  const [replyingPostId, setReplyingPostId] = useState<string | null>(null);
  const [upvotingKey, setUpvotingKey] = useState<string | null>(null);
  const [reportReason, setReportReason] = useState('');
  const memoryCacheRef = useRef<Map<string, CommunityCacheEntry>>(new Map());
  const inFlightGetRef = useRef<Map<string, Promise<unknown>>>(new Map());
  const refreshInFlightRef = useRef<Promise<void> | null>(null);
  const refreshQueuedRef = useRef(false);
  const submittedDiscoverQueryRef = useRef('');
  const quizRosterLoadedRef = useRef(false);
  const messageFileInputRef = useRef<HTMLInputElement | null>(null);
  const voiceRecorderRef = useRef<MediaRecorder | null>(null);
  const voiceStreamRef = useRef<MediaStream | null>(null);
  const voiceStartAtRef = useRef<number>(0);
  const hasInitializedChallengeNotifications = useRef(false);
  const previousChallengeState = useRef<Map<string, { status: string }>>(new Map());

  const getCacheStorageKey = useCallback((path: string) => `${COMMUNITY_CACHE_PREFIX}${path}`, []);

  const readCachedPayload = useCallback(<T,>(path: string): T | null => {
    const now = Date.now();
    const inMemory = memoryCacheRef.current.get(path);
    if (inMemory && inMemory.expiresAt > now) {
      return inMemory.payload as T;
    }

    try {
      const raw = localStorage.getItem(getCacheStorageKey(path));
      if (!raw) return null;
      const parsed = JSON.parse(raw) as CommunityCacheEntry;
      if (!parsed || typeof parsed.expiresAt !== 'number' || parsed.expiresAt <= now) {
        localStorage.removeItem(getCacheStorageKey(path));
        return null;
      }
      memoryCacheRef.current.set(path, parsed);
      return parsed.payload as T;
    } catch {
      return null;
    }
  }, [getCacheStorageKey]);

  const writeCachedPayload = useCallback((path: string, payload: unknown, ttlMs = COMMUNITY_CACHE_TTL_MS) => {
    const entry: CommunityCacheEntry = {
      expiresAt: Date.now() + Math.max(1_000, Math.floor(ttlMs)),
      payload,
    };
    memoryCacheRef.current.set(path, entry);
    try {
      localStorage.setItem(getCacheStorageKey(path), JSON.stringify(entry));
    } catch {
      // Ignore storage quota issues; in-memory cache still applies.
    }
  }, [getCacheStorageKey]);

  const invalidateCommunityCache = useCallback((prefix = '/api/community') => {
    for (const key of Array.from(memoryCacheRef.current.keys())) {
      if (key.startsWith(prefix)) {
        memoryCacheRef.current.delete(key);
      }
    }
    for (const key of Array.from(inFlightGetRef.current.keys())) {
      if (key.startsWith(prefix)) {
        inFlightGetRef.current.delete(key);
      }
    }
    try {
      const keysToDelete: string[] = [];
      for (let idx = 0; idx < localStorage.length; idx += 1) {
        const storageKey = localStorage.key(idx);
        if (!storageKey || !storageKey.startsWith(COMMUNITY_CACHE_PREFIX)) continue;
        const path = storageKey.slice(COMMUNITY_CACHE_PREFIX.length);
        if (path.startsWith(prefix)) {
          keysToDelete.push(storageKey);
        }
      }
      keysToDelete.forEach((storageKey) => localStorage.removeItem(storageKey));
    } catch {
      // Ignore storage read issues.
    }
  }, []);

  const requestCached = useCallback(async <T,>(path: string, options?: { force?: boolean; ttlMs?: number }): Promise<T> => {
    if (!token) {
      throw new Error('Authentication token is missing for community request.');
    }

    if (!options?.force) {
      const cached = readCachedPayload<T>(path);
      if (cached) return cached;
      const inFlight = inFlightGetRef.current.get(path);
      if (inFlight) {
        return inFlight as Promise<T>;
      }
    }

    const requestPromise = apiRequest<T>(path, {}, token)
      .then((payload) => {
        writeCachedPayload(path, payload, options?.ttlMs);
        return payload;
      })
      .finally(() => {
        inFlightGetRef.current.delete(path);
      });

    inFlightGetRef.current.set(path, requestPromise as Promise<unknown>);
    return requestPromise;
  }, [token, readCachedPayload, writeCachedPayload]);

  const activeConnection = useMemo(
    () => connections.find((item) => item.connectionId === activeConnectionId) || null,
    [connections, activeConnectionId],
  );

  const peerChatPresence = useMemo(() => {
    if (!activeConnection) return null;
    return onlineStudents.find((s) => s.id === activeConnection.user.id) || null;
  }, [activeConnection, onlineStudents]);

  const selectedQuizChallenge = useMemo(
    () => quizChallenges.find((item) => item.id === selectedQuizChallengeId) || null,
    [quizChallenges, selectedQuizChallengeId],
  );

  const userNameById = useMemo(() => {
    const map = new Map<string, string>();
    allCommunityUsers.forEach((row) => map.set(String(row.id), displayName(row)));
    connections.forEach((row) => map.set(String(row.user.id), displayName(row.user)));
    incomingRequests.forEach((row) => map.set(String(row.user.id), displayName(row.user)));
    outgoingRequests.forEach((row) => map.set(String(row.user.id), displayName(row.user)));
    return map;
  }, [allCommunityUsers, connections, incomingRequests, outgoingRequests]);

  const hasCommunityProfileData = useMemo(() => {
    return Boolean(
      usernameInput.trim()
      || bio.trim()
      || subjectsNeedHelpInput.trim()
      || profilePictureDataUrl.trim(),
    );
  }, [usernameInput, bio, subjectsNeedHelpInput, profilePictureDataUrl]);

  const loadLeaderboardAndBadges = useCallback(async (period: 'weekly' | 'monthly', force = false) => {
    if (!token) return;
    try {
      const [leaderboardPayload, badgesPayload] = await Promise.all([
        requestCached<{ leaderboard: LeaderboardRow[] }>(`/api/community/leaderboard?period=${period}`, { force }),
        requestCached<{ badges: BadgeRow[] }>('/api/community/achievements', { force }),
      ]);
      setLeaderboard(leaderboardPayload.leaderboard || []);
      setBadges(badgesPayload.badges || []);
    } catch {
      // Silent; tab-specific loads are best-effort.
    }
  }, [token, requestCached]);

  const loadQuizData = useCallback(async (force = false) => {
    if (!token) return;
    try {
      const [challengesPayload, quizBoardPayload] = await Promise.all([
        requestCached<{ challenges: QuizChallengeRow[] }>('/api/community/quiz-challenges', { force }),
        requestCached<{ leaderboard: QuizLeaderboardRow[] }>('/api/community/quiz-leaderboard', { force }),
      ]);

      const challengeRows = challengesPayload.challenges || [];
      setQuizChallenges(challengeRows);
      setQuizLeaderboard(quizBoardPayload.leaderboard || []);

      if (!selectedQuizChallengeId && challengeRows.length > 0) {
        setSelectedQuizChallengeId(challengeRows[0].id);
      } else if (selectedQuizChallengeId && !challengeRows.some((row) => row.id === selectedQuizChallengeId)) {
        setSelectedQuizChallengeId(challengeRows[0]?.id || '');
      }
    } catch {
      // Silent; quiz tab refreshes retry via polling.
    }
  }, [token, requestCached, selectedQuizChallengeId]);

  const challengeRemainingSeconds = useMemo(() => {
    if (!selectedQuizChallenge || !quizStartedAtMs) return 0;
    const elapsed = Math.floor((Date.now() - quizStartedAtMs) / 1000);
    return Math.max(0, Number(selectedQuizChallenge.durationSeconds || 0) - elapsed);
  }, [selectedQuizChallenge, quizStartedAtMs]);

  const loadDiscussionRoomPosts = useCallback(async (roomId: string, force = false) => {
    if (!token || !roomId) return;
    const payload = await requestCached<{ room: DiscussionRoom; posts: DiscussionPost[] }>(`/api/community/discussion-rooms/${roomId}/posts`, { force });
    setRoomPosts(payload.posts || []);
  }, [token, requestCached]);

  const refreshDiscussionFeed = useCallback(async (force = false) => {
    if (!token || !rooms.length) return;
    const lists = await Promise.all(rooms.map(async (room) => {
      try {
        const payload = await requestCached<{ posts: DiscussionPost[] }>(`/api/community/discussion-rooms/${room.id}/posts`, { force, ttlMs: 10_000 });
        return payload.posts || [];
      } catch {
        return [] as DiscussionPost[];
      }
    }));
    const merged = lists.flat().sort((a, b) => {
      const at = a.createdAt ? new Date(a.createdAt).getTime() : 0;
      const bt = b.createdAt ? new Date(b.createdAt).getTime() : 0;
      return bt - at;
    });
    setFeedPosts(merged);
  }, [token, rooms, requestCached]);

  useEffect(() => {
    if (activeTab !== 'discussion-rooms') return;
    void refreshDiscussionFeed(false);
  }, [activeTab, refreshDiscussionFeed]);

  /**
   * Presence roster: the server snapshot is authoritative. Event-driven reconciliations are
   * silent (no "Refreshing…" state) and merge by stable user id so unchanged tiles keep their
   * object identity. Only the manual "Refresh roster" button shows a loading state.
   */
  const loadPresence = useCallback(async (options?: { silent?: boolean }) => {
    if (!token) return;
    const silent = options?.silent !== false;
    const seq = ++presenceRequestSeqRef.current;
    if (!silent) setPresenceLoading(true);
    try {
      const payload = await apiRequest<{ online: OnlineStudentRow[] }>('/api/community/presence', {}, token);
      // A newer request already started; let it win to avoid applying a stale snapshot.
      if (seq !== presenceRequestSeqRef.current) return;
      const others = withoutSelfOnlineStudents(payload.online || [], selfPresenceKeysRef.current);
      setOnlineStudents((prev) => mergePresenceRoster(prev, others));
      setPresenceResolved(true);
    } catch {
      // Keep the last known roster; a transient failure must not blank the list.
    } finally {
      if (!silent && seq === presenceRequestSeqRef.current) setPresenceLoading(false);
    }
  }, [token]);

  useEffect(() => {
    loadPresenceRef.current = loadPresence;
  }, [loadPresence]);

  /** Coalesce bursts of presence events (many users pinging) into one quiet reconciliation. */
  const schedulePresenceReconcile = useCallback((delayMs = 250) => {
    if (presenceReconcileTimerRef.current != null) return;
    presenceReconcileTimerRef.current = window.setTimeout(() => {
      presenceReconcileTimerRef.current = null;
      void loadPresenceRef.current({ silent: true });
    }, delayMs);
  }, []);

  useEffect(() => {
    return () => {
      if (presenceReconcileTimerRef.current != null) {
        window.clearTimeout(presenceReconcileTimerRef.current);
        presenceReconcileTimerRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    activeConnectionIdRef.current = activeConnectionId;
  }, [activeConnectionId]);

  const refreshActiveMessages = useCallback(async () => {
    if (!token || !activeConnectionId) return;
    const payload = await requestCached<{ messages: MessageRow[] }>(`/api/community/messages/${activeConnectionId}`, { force: true, ttlMs: 10_000 });
    setMessages(payload.messages || []);
  }, [token, activeConnectionId, requestCached]);

  const refreshActiveMessagesRef = useRef(refreshActiveMessages);
  useEffect(() => {
    refreshActiveMessagesRef.current = refreshActiveMessages;
  }, [refreshActiveMessages]);

  const postTyping = useCallback(
    async (typing: boolean) => {
      if (!token || !activeConnectionId) return;
      try {
        await apiRequest(
          `/api/community/messages/${activeConnectionId}/typing`,
          { method: 'POST', body: JSON.stringify({ typing }) },
          token,
        );
      } catch {
        // Typing hints are best-effort.
      }
    },
    [token, activeConnectionId],
  );

  const bumpTypingFromInput = useCallback(() => {
    if (typingEmitTimerRef.current) window.clearTimeout(typingEmitTimerRef.current);
    typingEmitTimerRef.current = window.setTimeout(() => {
      void postTyping(true);
      typingEmitTimerRef.current = null;
    }, 450);
    if (typingIdleTimerRef.current) window.clearTimeout(typingIdleTimerRef.current);
    typingIdleTimerRef.current = window.setTimeout(() => {
      void postTyping(false);
      typingIdleTimerRef.current = null;
    }, 2200);
  }, [postTyping]);

  const refreshCommunity = useCallback(async (force = false) => {
    if (!token) return;

    if (refreshInFlightRef.current) {
      refreshQueuedRef.current = true;
      return refreshInFlightRef.current;
    }

    refreshInFlightRef.current = (async () => {
      let passForce = force;
      do {
      refreshQueuedRef.current = false;
      const [
        profilePayload,
        requestsPayload,
        connectionsPayload,
        roomsPayload,
        partnersPayload,
      ] = await Promise.all([
        requestCached<{ profile: CommunityUser }>('/api/community/profile', { force: passForce }),
        requestCached<{ incoming: CommunityRequestRow[]; outgoing: CommunityRequestRow[] }>('/api/community/connections/requests', { force: passForce }),
        requestCached<{ connections: ConnectionRow[] }>('/api/community/connections', { force: passForce }),
        requestCached<{ rooms: DiscussionRoom[] }>('/api/community/discussion-rooms', { force: passForce }),
        requestCached<{ studyPartners: Array<{ compatibility: number; user: CommunityUser; reasons?: string[] }> }>('/api/community/study-partners', { force: passForce }),
      ]);

      setProfile(profilePayload.profile || null);
      setUsernameInput(profilePayload.profile?.username || '');
      setProfilePictureDataUrl(profilePayload.profile?.profilePictureUrl || '');
      setProfilePictureUploadName('');
      setShareProfilePicture(Boolean(profilePayload.profile?.shareProfilePicture));
      setTargetNetType(profilePayload.profile?.targetNetType || 'net-engineering');
      setSubjectsNeedHelpInput((profilePayload.profile?.subjectsNeedHelp || []).join(', '));
      setPreparationLevel((profilePayload.profile?.preparationLevel as 'beginner' | 'intermediate' | 'advanced') || 'intermediate');
      setStudyTimePreference((profilePayload.profile?.studyTimePreference as 'morning' | 'evening' | 'night' | 'flexible') || 'flexible');
      setScoreRangeMin(Number(profilePayload.profile?.testScoreRange?.min ?? 0));
      setScoreRangeMax(Number(profilePayload.profile?.testScoreRange?.max ?? 200));
      setBio(profilePayload.profile?.bio || '');
      setHideOnlineStatus(Boolean(profilePayload.profile?.hideOnlineStatus));
      setDoNotDisturb(Boolean(profilePayload.profile?.doNotDisturb));
      setInterestsInput((profilePayload.profile?.communityInterests || []).join(', '));
      setIsCommunityProfileExpanded(!(profilePayload.profile?.username || profilePayload.profile?.bio));

      setIncomingRequests(requestsPayload.incoming || []);
      setOutgoingRequests(requestsPayload.outgoing || []);
      setConnections(connectionsPayload.connections || []);
      setRooms(roomsPayload.rooms || []);
      setStudyPartners(partnersPayload.studyPartners || []);

      const nextRoomId = activeRoomId || (roomsPayload.rooms?.[0]?.id || '');
      setActiveRoomId(nextRoomId);
      if (nextRoomId) {
        await loadDiscussionRoomPosts(nextRoomId, passForce);
      } else {
        setRoomPosts([]);
      }

      const nextConnectionId = activeConnectionId || (connectionsPayload.connections?.[0]?.connectionId || '');
      setActiveConnectionId(nextConnectionId);
      if (nextConnectionId) {
        const messagePayload = await requestCached<{ messages: MessageRow[] }>(`/api/community/messages/${nextConnectionId}`, { force: passForce, ttlMs: 15_000 });
        setMessages(messagePayload.messages || []);
      } else {
        setMessages([]);
      }
      void loadPresence({ silent: true });
      passForce = true;
      } while (refreshQueuedRef.current);
    })().finally(() => {
      refreshInFlightRef.current = null;
    });

    return refreshInFlightRef.current;
  }, [token, requestCached, activeRoomId, activeConnectionId, loadDiscussionRoomPosts, loadPresence]);

  const refreshCommunityRef = useRef(refreshCommunity);
  const invalidateCommunityCacheRef = useRef(invalidateCommunityCache);
  useEffect(() => {
    refreshCommunityRef.current = refreshCommunity;
  }, [refreshCommunity]);
  useEffect(() => {
    invalidateCommunityCacheRef.current = invalidateCommunityCache;
  }, [invalidateCommunityCache]);

  useLayoutEffect(() => {
    if (!token) return;
    const profilePayload = readExpiredCommunityCachePayload<{ profile: CommunityUser }>('/api/community/profile');
    if (profilePayload?.profile) {
      const p = profilePayload.profile;
      setProfile(p);
      setUsernameInput(p.username || '');
      setProfilePictureDataUrl(p.profilePictureUrl || '');
      setProfilePictureUploadName('');
      setShareProfilePicture(Boolean(p.shareProfilePicture));
      setTargetNetType(p.targetNetType || 'net-engineering');
      setSubjectsNeedHelpInput((p.subjectsNeedHelp || []).join(', '));
      setPreparationLevel((p.preparationLevel as 'beginner' | 'intermediate' | 'advanced') || 'intermediate');
      setStudyTimePreference((p.studyTimePreference as 'morning' | 'evening' | 'night' | 'flexible') || 'flexible');
      setScoreRangeMin(Number(p.testScoreRange?.min ?? 0));
      setScoreRangeMax(Number(p.testScoreRange?.max ?? 200));
      setBio(p.bio || '');
      setHideOnlineStatus(Boolean(p.hideOnlineStatus));
      setDoNotDisturb(Boolean(p.doNotDisturb));
      setInterestsInput((p.communityInterests || []).join(', '));
      setIsCommunityProfileExpanded(!(p.username || p.bio));
    }

    const requestsPayload = readExpiredCommunityCachePayload<{ incoming: CommunityRequestRow[]; outgoing: CommunityRequestRow[] }>(
      '/api/community/connections/requests',
    );
    if (requestsPayload) {
      setIncomingRequests(requestsPayload.incoming || []);
      setOutgoingRequests(requestsPayload.outgoing || []);
    }

    const connectionsPayload = readExpiredCommunityCachePayload<{ connections: ConnectionRow[] }>('/api/community/connections');
    if (connectionsPayload?.connections?.length) {
      setConnections(connectionsPayload.connections);
      setActiveConnectionId((prev) => prev || connectionsPayload.connections[0]?.connectionId || '');
    }

    const roomsPayload = readExpiredCommunityCachePayload<{ rooms: DiscussionRoom[] }>('/api/community/discussion-rooms');
    if (roomsPayload?.rooms?.length) {
      setRooms(roomsPayload.rooms);
      setActiveRoomId((prev) => prev || roomsPayload.rooms[0]?.id || '');
    }

    const partnersPayload = readExpiredCommunityCachePayload<{
      studyPartners: Array<{ compatibility: number; user: CommunityUser; reasons?: string[] }>;
    }>('/api/community/study-partners');
    if (partnersPayload?.studyPartners) {
      setStudyPartners(partnersPayload.studyPartners);
    }

    const lbWeekly = readExpiredCommunityCachePayload<{ leaderboard: LeaderboardRow[] }>('/api/community/leaderboard?period=weekly');
    if (lbWeekly?.leaderboard?.length) {
      setLeaderboard(lbWeekly.leaderboard);
    }
    const badgesCached = readExpiredCommunityCachePayload<{ badges: BadgeRow[] }>('/api/community/achievements');
    if (badgesCached?.badges?.length) {
      setBadges(badgesCached.badges);
    }

    const quizCh = readExpiredCommunityCachePayload<{ challenges: QuizChallengeRow[] }>('/api/community/quiz-challenges');
    const quizLb = readExpiredCommunityCachePayload<{ leaderboard: QuizLeaderboardRow[] }>('/api/community/quiz-leaderboard');
    if (quizCh?.challenges?.length) {
      setQuizChallenges(quizCh.challenges);
      const firstId = quizCh.challenges[0]?.id || '';
      setSelectedQuizChallengeId((prev) => prev || firstId);
    }
    if (quizLb?.leaderboard?.length) {
      setQuizLeaderboard(quizLb.leaderboard);
    }
  }, [token]);

  // Initial load, once per signed-in user. Deliberately NOT keyed on `refreshCommunity`/`token`:
  // `refreshCommunity` changes identity whenever the active room/connection changes and `token`
  // rotates on every silent refresh — both used to re-run this effect and reload everything,
  // which is where the repeated "Could not load community data." toasts came from when any of
  // the parallel requests failed mid-session. Socket events + the reconcile poll keep data fresh.
  const initialLoadUserId = String(user?.id || '');
  useEffect(() => {
    if (!token || !initialLoadUserId) return;

    let cancelled = false;
    void (async () => {
      try {
        await refreshCommunityRef.current(false);
      } catch (error) {
        if (!cancelled) {
          handleApiError(error, 'Could not load community data.');
        }
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialLoadUserId, Boolean(token)]);

  useEffect(() => {
    if (!token) return;
    if (activeTab === 'leaderboard') {
      void loadLeaderboardAndBadges(leaderboardPeriod, false);
      return;
    }
    if (activeTab === 'quiz-battles') {
      void loadQuizData(false);
    }
  }, [token, activeTab, leaderboardPeriod, loadLeaderboardAndBadges, loadQuizData]);

  useEffect(() => {
    if (!token || !activeRoomId) {
      setRoomPosts([]);
      return;
    }
    void loadDiscussionRoomPosts(activeRoomId, false);
  }, [token, activeRoomId, loadDiscussionRoomPosts]);

  useEffect(() => {
    if (!token || !activeConnectionId) {
      setMessages([]);
      setMessagesThreadLoading(false);
      return;
    }
    let cancelled = false;
    async function loadMessages() {
      setMessagesThreadLoading(true);
      try {
        const payload = await requestCached<{ messages: MessageRow[] }>(`/api/community/messages/${activeConnectionId}`, { ttlMs: 15_000 });
        if (!cancelled) setMessages(payload.messages || []);
      } catch {
        if (!cancelled) setMessages([]);
      } finally {
        if (!cancelled) setMessagesThreadLoading(false);
      }
    }
    void loadMessages();
    return () => {
      cancelled = true;
    };
  }, [token, activeConnectionId, requestCached]);

  useEffect(() => {
    if (activeTab !== 'messages' || !activeConnectionId) return;
    const el = messageThreadEndRef.current;
    if (!el) return;
    const id = window.requestAnimationFrame(() => {
      el.scrollIntoView({ behavior: 'smooth', block: 'end' });
    });
    return () => window.cancelAnimationFrame(id);
  }, [messages, remoteTyping, activeConnectionId, activeTab]);

  useEffect(() => {
    if (!token || activeTab !== 'messages' || !activeConnectionId) return;
    const onVis = () => {
      if (document.hidden) {
        markNativeDocumentHidden();
        return;
      }
      if (consumeBriefNativeHide()) return;
      void refreshActiveMessages();
    };
    const onFocus = () => {
      if (consumeBriefNativeHide()) return;
      void refreshActiveMessages();
    };
    document.addEventListener('visibilitychange', onVis);
    window.addEventListener('focus', onFocus);
    return () => {
      document.removeEventListener('visibilitychange', onVis);
      window.removeEventListener('focus', onFocus);
    };
  }, [token, activeTab, activeConnectionId, refreshActiveMessages]);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    let running = false;

    const poll = async () => {
      if (document.hidden) return;
      if (running || cancelled) return;
      running = true;
      try {
        const [requestsPayload, connectionsPayload, roomsPayload] = await Promise.all([
          requestCached<{ incoming: CommunityRequestRow[]; outgoing: CommunityRequestRow[] }>('/api/community/connections/requests', { force: true, ttlMs: 10_000 }),
          requestCached<{ connections: ConnectionRow[] }>('/api/community/connections', { force: true, ttlMs: 10_000 }),
          requestCached<{ rooms: DiscussionRoom[] }>('/api/community/discussion-rooms', { force: true, ttlMs: 10_000 }),
        ]);

        if (cancelled) return;
        setIncomingRequests(requestsPayload.incoming || []);
        setOutgoingRequests(requestsPayload.outgoing || []);
        setConnections(connectionsPayload.connections || []);
        setRooms(roomsPayload.rooms || []);

        if (activeConnectionId) {
          const messagePayload = await requestCached<{ messages: MessageRow[] }>(`/api/community/messages/${activeConnectionId}`, { force: true, ttlMs: 10_000 });
          if (!cancelled) setMessages(messagePayload.messages || []);
        }

        if (activeRoomId) {
          const roomPayload = await requestCached<{ posts: DiscussionPost[] }>(`/api/community/discussion-rooms/${activeRoomId}/posts`, { force: true, ttlMs: 10_000 });
          if (!cancelled) setRoomPosts(roomPayload.posts || []);
        }

        if (activeTab === 'leaderboard') {
          await loadLeaderboardAndBadges(leaderboardPeriod, true);
        }
        if (activeTab === 'quiz-battles') {
          await loadQuizData(true);
        }
      } catch {
        // Silent polling failures; primary actions already show toasts.
      } finally {
        running = false;
      }
    };

    // Socket.IO `sync` events are the primary update path (the server emits
    // community.message.*, community.connection.*, community.discussion.*, community.quiz.*).
    // Poll only as a bounded fallback while the realtime channel is down; otherwise reconcile
    // quietly once a minute.
    const intervalMs = realtimeDown ? 15_000 : 60_000;
    const intervalId = window.setInterval(() => {
      if (document.hidden) return;
      void poll();
    }, intervalMs);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [token, activeConnectionId, activeRoomId, activeTab, leaderboardPeriod, requestCached, loadLeaderboardAndBadges, loadQuizData, realtimeDown]);

  useEffect(() => {
    return () => {
      if (voiceRecorderRef.current && voiceRecorderRef.current.state !== 'inactive') {
        voiceRecorderRef.current.stop();
      }
      if (voiceStreamRef.current) {
        voiceStreamRef.current.getTracks().forEach((track) => track.stop());
      }
    };
  }, []);

  const studyingSubjectPingRef = useRef('');
  useEffect(() => {
    studyingSubjectPingRef.current = studyingSubjectPing;
  }, [studyingSubjectPing]);

  // Presence heartbeat (away flag + "currently studying"). The server broadcasts an update to
  // every student on each ping, so this must not fire per keystroke: the interval reads the
  // latest subject from a ref and subject edits are debounced separately below.
  useEffect(() => {
    if (!token) return;

    const ping = () => {
      void apiRequest(
        '/api/community/presence/ping',
        {
          method: 'POST',
          body: JSON.stringify({
            away: document.hidden,
            studyingSubject: studyingSubjectPingRef.current.trim() || undefined,
          }),
        },
        token,
      ).catch(() => {});
    };
    pingPresenceRef.current = ping;

    ping();
    const id = window.setInterval(() => {
      if (document.hidden) return;
      ping();
    }, 45_000);
    const onVis = () => ping();
    document.addEventListener('visibilitychange', onVis);
    return () => {
      window.clearInterval(id);
      document.removeEventListener('visibilitychange', onVis);
      pingPresenceRef.current = () => {};
    };
  }, [token]);

  // Debounced publish of "currently studying" edits (one ping after typing pauses).
  const studyingSubjectDirtyRef = useRef(false);
  useEffect(() => {
    if (!token) return;
    if (!studyingSubjectDirtyRef.current) {
      studyingSubjectDirtyRef.current = true;
      return;
    }
    const timer = window.setTimeout(() => {
      pingPresenceRef.current();
    }, 700);
    return () => window.clearTimeout(timer);
  }, [token, studyingSubjectPing]);

  // Keep the shared socket's credentials current without recreating it (a token rotation must
  // never tear the socket down — that showed as offline→online flicker for everyone else).
  useEffect(() => {
    setRealtimeAuthToken('student', token);
  }, [token]);

  const hasAuth = Boolean(token);
  const authUserId = String(user?.id || '');

  // Subscribe to the ONE shared Socket.IO connection (see lib/realtimeSocket.ts). This effect
  // only attaches/detaches listeners; the connection itself outlives the Community page.
  useEffect(() => {
    if (!hasAuth || !authUserId) return;

    let closed = false;
    let disconnectToastShown = false;
    let fullRefreshTimer: number | null = null;

    const scheduleFullCommunityRefresh = () => {
      if (document.hidden) return;
      if (fullRefreshTimer != null) return;
      fullRefreshTimer = window.setTimeout(() => {
        fullRefreshTimer = null;
        void refreshCommunityRef.current(true).catch(() => undefined);
      }, 160);
    };

    const applyCommunityPayload = (parsed: {
      type?: string;
      action?: string;
      connectionId?: string;
      typing?: boolean;
      userId?: string;
      status?: string;
      activity?: string;
      studyingSubject?: string;
      displayName?: string;
      username?: string;
      lastSeenAt?: string;
      message?: MessageRow;
    }) => {
      const t = String(parsed.type || '');
      // The shared socket also carries non-community events (support chat, subscription,
      // profile...). Only community.* events may trigger a Community reload.
      if (!t.startsWith('community.')) return;
      if (t === 'community.typing' && parsed.connectionId === activeConnectionIdRef.current) {
        if (typingStopTimerRef.current) window.clearTimeout(typingStopTimerRef.current);
        startTransition(() => {
          setRemoteTyping(Boolean(parsed.typing));
        });
        if (parsed.typing) {
          typingStopTimerRef.current = window.setTimeout(() => {
            startTransition(() => setRemoteTyping(false));
          }, 3200);
        }
        return;
      }
      if (t === 'community.presence') {
        const uid = String(parsed.userId || '').trim();
        // Our own presence changes are not part of our roster.
        if (isSelfOnlineStudent({ id: uid, userId: uid, username: parsed.username }, selfPresenceKeysRef.current)) {
          setOnlineStudents((prev) => prev);
          return;
        }
        if (parsed.action === 'offline' && uid) {
          setOnlineStudents((prev) => (
            prev.some((row) => String(row.id) === uid || String(row.userId) === uid)
              ? prev.filter((row) => String(row.id) !== uid && String(row.userId) !== uid)
              : prev
          ));
          return;
        }
        if (parsed.action === 'update' && uid) {
          let known = false;
          setOnlineStudents((prev) => {
            known = prev.some((row) => String(row.id) === uid || String(row.userId) === uid);
            if (!known) return prev;
            return prev.map((row) => {
              if (String(row.id) !== uid && String(row.userId) !== uid) return row;
              const named = parsed.displayName ? onlineRowFromPresenceEvent(uid, parsed) : null;
              return {
                ...row,
                ...(named ? { firstName: named.firstName || row.firstName, lastName: named.lastName || row.lastName, username: named.username || row.username } : {}),
                presenceStatus: parsed.status === 'away' ? 'away' : 'online',
                activity: parsed.activity !== undefined ? String(parsed.activity) : row.activity,
                studyingSubject: parsed.studyingSubject !== undefined ? String(parsed.studyingSubject) : row.studyingSubject,
                lastSeenAt: parsed.lastSeenAt || row.lastSeenAt,
              };
            });
          });
          if (!known) schedulePresenceReconcile(0);
          return;
        }
        if (parsed.action === 'online' && uid) {
          setOnlineStudents((prev) => (
            prev.some((row) => String(row.id) === uid || String(row.userId) === uid)
              ? prev
              : [onlineRowFromPresenceEvent(uid, parsed), ...prev]
          ));
        }
        schedulePresenceReconcile(0);
        return;
      }
      if (t === 'community.message.read') {
        if (String(parsed.connectionId || '') === activeConnectionIdRef.current) {
          void refreshActiveMessagesRef.current();
        }
        return;
      }
      if (t === 'community.message.sent') {
        const incoming = parsed.message;
        if (incoming?.id && String(incoming.connectionId || parsed.connectionId || '') === activeConnectionIdRef.current) {
          setMessages((prev) => upsertCommunityMessage(prev, incoming));
        }
        invalidateCommunityCacheRef.current('/api/community/messages');
        invalidateCommunityCacheRef.current('/api/community/connections');
        if (document.hidden) return;
        scheduleFullCommunityRefresh();
        return;
      }
      if (t.startsWith('community.connection.')) {
        invalidateCommunityCacheRef.current('/api/community/connections');
        invalidateCommunityCacheRef.current('/api/community');
        if (document.hidden) return;
        scheduleFullCommunityRefresh();
        return;
      }
      if (document.hidden) return;
      scheduleFullCommunityRefresh();
    };

    const socket = acquireRealtimeSocket('student');

    const onConnect = () => {
      logNativeEvent('socket', 'community-connect', { recovered: Boolean(socket.recovered) });
      setRealtimeDown(false);
      disconnectToastShown = false;
      // The server registered our presence on this handshake; restore our away/"studying" meta
      // and reconcile the roster in case events were missed while disconnected.
      pingPresenceRef.current();
      schedulePresenceReconcile(0);
    };

    const onDisconnect = (reason: string) => {
      logNativeEvent('socket', 'community-disconnect', { reason: String(reason || 'socket-disconnect') }, 'warn');
      if (closed || reason === 'io client disconnect') return;
      setRealtimeDown(true);
      if (!disconnectToastShown) {
        disconnectToastShown = true;
        showNeutralToast('Connection lost. Reconnecting…');
      }
    };

    // auth.session_revoked / subscription.refresh are forwarded app-wide by lib/realtimeSocket.ts.
    const onSync = (data: unknown) => {
      const parsed = (data && typeof data === 'object')
        ? (data as { type?: string; action?: string; connectionId?: string; typing?: boolean; userId?: string })
        : {};
      applyCommunityPayload(parsed);
    };

    socket.on('connect', onConnect);
    socket.on('disconnect', onDisconnect);
    socket.on('sync', onSync);
    const unsubscribeStatus = subscribeRealtimeStatus('student', (status) => {
      if (closed) return;
      setRealtimeDown(status !== 'connected');
    });

    // The shared socket may already be connected (e.g. Support Chat acquired it first, or we
    // are returning to this page): run the same resync as a fresh connect would.
    if (socket.connected) {
      setRealtimeDown(false);
      pingPresenceRef.current();
      schedulePresenceReconcile(0);
    } else {
      setRealtimeDown(true);
    }

    const handleConnectivityResume = () => {
      if (closed) return;
      logNativeEvent('socket', 'community-resume-reconnect');
      reconnectRealtimeIfNeeded('student');
      schedulePresenceReconcile(0);
    };
    const onOnline = () => {
      handleConnectivityResume();
    };
    const onVisibilityChange = () => {
      if (document.hidden) {
        markNativeDocumentHidden();
        return;
      }
      if (consumeBriefNativeHide()) return;
      handleConnectivityResume();
    };
    window.addEventListener('online', onOnline);
    document.addEventListener('visibilitychange', onVisibilityChange);
    const appStateListenerPromise = CapacitorApp
      .addListener('appStateChange', ({ isActive }) => {
        if (!isActive) {
          markNativeDocumentHidden();
          return;
        }
        if (consumeBriefNativeHide()) return;
        handleConnectivityResume();
      })
      .catch(() => null);

    return () => {
      closed = true;
      window.removeEventListener('online', onOnline);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      void appStateListenerPromise.then((listener) => listener?.remove());
      unsubscribeStatus();
      socket.off('connect', onConnect);
      socket.off('disconnect', onDisconnect);
      socket.off('sync', onSync);
      releaseRealtimeSocket('student', socket);
      if (typingStopTimerRef.current) window.clearTimeout(typingStopTimerRef.current);
      if (fullRefreshTimer != null) window.clearTimeout(fullRefreshTimer);
    };
  }, [hasAuth, authUserId, schedulePresenceReconcile]);

  useEffect(() => {
    return () => {
      if (typingEmitTimerRef.current) window.clearTimeout(typingEmitTimerRef.current);
      if (typingIdleTimerRef.current) window.clearTimeout(typingIdleTimerRef.current);
    };
  }, []);

  useEffect(() => {
    const currentMap = new Map(quizChallenges.map((challenge) => [challenge.id, { status: String(challenge.status || '') }]));

    if (!hasInitializedChallengeNotifications.current) {
      previousChallengeState.current = currentMap;
      hasInitializedChallengeNotifications.current = true;
      return;
    }

    const previousMap = previousChallengeState.current;
    for (const challenge of quizChallenges) {
      const previous = previousMap.get(challenge.id);
      const opponentUserId = challenge.isChallenger ? challenge.opponentUserId : challenge.challengerUserId;
      const opponentName = userNameById.get(String(opponentUserId)) || 'Student';

      if (!previous && !challenge.isChallenger && challenge.status === 'pending' && challenge.challengeType === 'async') {
        showInfoToast(`New async challenge from ${opponentName}.`);
      }

      if (
        challenge.isChallenger
        && previous?.status === 'pending'
        && ['accepted', 'in_progress'].includes(String(challenge.status || ''))
      ) {
        showSuccessToast(`${opponentName} accepted your challenge.`);
      }
    }

    previousChallengeState.current = currentMap;
  }, [quizChallenges, userNameById]);

  const syncLiveAnswerLock = async (questionId: string, selectedOption: string) => {
    if (!token || !selectedQuizChallenge || selectedQuizChallenge.challengeType !== 'live') return;
    if (!['accepted', 'in_progress'].includes(selectedQuizChallenge.status)) return;
    if (selectedQuizChallenge.myResult?.submitted) return;

    const elapsedSeconds = quizStartedAtMs ? Math.max(0, Math.floor((Date.now() - quizStartedAtMs) / 1000)) : 0;
    try {
      await apiRequest(`/api/community/quiz-challenges/${selectedQuizChallenge.id}/progress`, {
        method: 'POST',
        body: JSON.stringify({ questionId, selectedOption, elapsedSeconds }),
      }, token);
    } catch {
      // Keep quiz flow smooth even if transient sync fails.
    }
  };

  const onProfilePictureSelected = async (event: ChangeEvent<HTMLInputElement>) => {
    const selected = event.target.files?.[0] || null;
    if (!selected) return;

    const mimeType = String(selected.type || '').toLowerCase();
    if (!PROFILE_PICTURE_ALLOWED_MIME_TYPES.has(mimeType)) {
      showErrorToast('Profile picture format not supported. Use JPG, PNG, WEBP, GIF, or SVG.');
      event.currentTarget.value = '';
      return;
    }

    if (selected.size > PROFILE_PICTURE_MAX_BYTES) {
      showErrorToast('Profile picture exceeds 3MB limit.');
      event.currentTarget.value = '';
      return;
    }

    try {
      const dataUrl = await fileToDataUrl(selected);
      setProfilePictureDataUrl(dataUrl);
      setProfilePictureUploadName(selected.name);
      showSuccessToast('Profile picture selected. Save profile to apply.');
    } catch {
      showErrorToast('Could not read selected profile picture.');
    } finally {
      event.currentTarget.value = '';
    }
  };

  const saveCommunityProfile = async () => {
    if (!token || isSavingCommunityProfile) return;
    setIsSavingCommunityProfile(true);
    try {
      const subjectsNeedHelp = subjectsNeedHelpInput
        .split(',')
        .map((item) => item.trim().toLowerCase())
        .filter(Boolean)
        .slice(0, 10);

      const payload = await apiRequest<{ profile: CommunityUser }>(
        '/api/community/profile',
        {
          method: 'PUT',
          body: JSON.stringify({
            username: usernameInput,
            profilePictureDataUrl,
            shareProfilePicture,
            targetNetType,
            subjectsNeedHelp,
            preparationLevel,
            studyTimePreference,
            testScoreRange: { min: scoreRangeMin, max: scoreRangeMax },
            bio,
            hideOnlineStatus,
            doNotDisturb,
            communityInterests: interestsInput
              .split(',')
              .map((item) => item.trim().toLowerCase())
              .filter(Boolean)
              .slice(0, 12),
          }),
        },
        token,
      );
      setProfile(payload.profile);
      setProfilePictureDataUrl(payload.profile?.profilePictureUrl || profilePictureDataUrl);
      setProfilePictureUploadName('');
      setIsCommunityProfileExpanded(false);
      showSuccessToast('Community profile updated.');
      invalidateCommunityCache('/api/community');
      await refreshCommunity(true);
    } catch (error) {
      handleApiError(error, 'Could not update profile.');
    } finally {
      setIsSavingCommunityProfile(false);
    }
  };

  const searchUsers = useCallback(async (query = searchQuery, force = false, silent = false) => {
    if (!token) return;
    const trimmed = String(query || '').trim();
    if (!trimmed) return;
    submittedDiscoverQueryRef.current = trimmed;
    setSearchLoading(true);
    try {
      const payload = await requestCached<{ users: Array<CommunityUser & { connectionStatus?: string }> }>(
        `/api/community/users/search?q=${encodeURIComponent(trimmed)}`,
        { force, ttlMs: 30_000 },
      );
      setSearchResults(payload.users || []);
      setDiscoverSearched(true);
    } catch (error) {
      if (!silent) {
        handleApiError(error, 'Could not search users.');
      }
    } finally {
      setSearchLoading(false);
    }
  }, [token, requestCached, searchQuery]);

  useEffect(() => {
    if (!token || activeTab !== 'quiz-battles' || quizRosterLoadedRef.current) return;
    let cancelled = false;
    void requestCached<{ users: Array<CommunityUser & { connectionStatus?: string }> }>(
      '/api/community/users/search?roster=1',
      { ttlMs: 60_000 },
    ).then((payload) => {
      if (cancelled) return;
      quizRosterLoadedRef.current = true;
      setAllCommunityUsers(payload.users || []);
    }).catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [token, activeTab, requestCached]);

  const connectingUserIdsRef = useRef<Set<string>>(new Set());
  const sendConnectionRequest = async (toUserId: string) => {
    if (!token) return;
    // One request per user at a time: a double click must not send two invitations.
    if (connectingUserIdsRef.current.has(toUserId)) return;
    const status = [
      ...onlineStudents,
      ...searchResults,
      ...studyPartners.map((item) => item.user),
      ...allCommunityUsers,
    ].find((row) => row.id === toUserId)?.connectionStatus;

    if (!canSendConnectionRequest(status)) {
      showInfoToast(status === 'connected' ? 'Already connected.' : 'Connection request already exists.');
      return;
    }

    connectingUserIdsRef.current.add(toUserId);
    setConnectingUserIds(new Set(connectingUserIdsRef.current));
    try {
      await apiRequest('/api/community/connections/request', {
        method: 'POST',
        body: JSON.stringify({ toUserId }),
      }, token);
      showSuccessToast('Connection request sent.');
      setOnlineStudents((prev) => prev.map((row) => (
        row.id === toUserId ? { ...row, connectionStatus: 'pending-sent' } : row
      )));
      invalidateCommunityCache('/api/community');
      const submittedQuery = submittedDiscoverQueryRef.current;
      await Promise.all([
        refreshCommunity(true),
        submittedQuery ? searchUsers(submittedQuery, true, true) : Promise.resolve(),
      ]);
    } catch (error) {
      handleApiError(error, 'Could not send request.');
    } finally {
      connectingUserIdsRef.current.delete(toUserId);
      setConnectingUserIds(new Set(connectingUserIdsRef.current));
    }
  };

  const connectButtonLabel = (status: string | undefined, userId: string) => {
    if (connectingUserIds.has(userId)) return 'Connecting…';
    if (status === 'connected') return 'Connected';
    if (status === 'pending-sent') return 'Request sent';
    if (status === 'pending-received') return 'Respond to request';
    return 'Connect';
  };

  const respondToRequest = async (requestId: string, action: 'accept' | 'reject') => {
    if (!token) return;
    try {
      await apiRequest(`/api/community/connections/requests/${requestId}/respond`, {
        method: 'POST',
        body: JSON.stringify({ action }),
      }, token);
      showSuccessToast(action === 'accept' ? 'Connection accepted.' : 'Request rejected.');
      invalidateCommunityCache('/api/community');
      await refreshCommunity(true);
    } catch (error) {
      handleApiError(error, 'Could not update request.');
    }
  };

  const createRoomPost = async () => {
    if (!token || !activeRoomId || isPostingRoom) return;
    if (!newPostText.trim()) {
      showErrorToast('Write your discussion or doubt before posting.');
      return;
    }
    setIsPostingRoom(true);
    try {
      await apiRequest(`/api/community/discussion-rooms/${activeRoomId}/posts`, {
        method: 'POST',
        body: JSON.stringify({ type: newPostType, title: newPostTitle, text: newPostText }),
      }, token);
      setNewPostTitle('');
      setNewPostText('');
      showSuccessToast('Posted to discussion room.');
      invalidateCommunityCache('/api/community/discussion-rooms');
      await loadDiscussionRoomPosts(activeRoomId, true);
      const roomsPayload = await requestCached<{ rooms: DiscussionRoom[] }>('/api/community/discussion-rooms', { force: true });
      setRooms(roomsPayload.rooms || []);
    } catch (error) {
      handleApiError(error, 'Could not post to room.');
    } finally {
      setIsPostingRoom(false);
    }
  };

  const publishTopic = async () => {
    const roomId = rooms[0]?.id || activeRoomId;
    if (!token || !roomId || isPostingRoom) return;
    if (!newPostTitle.trim()) {
      showErrorToast('Add a topic for your post.');
      return;
    }
    if (!newPostText.trim()) {
      showErrorToast('Write your post.');
      return;
    }
    setIsPostingRoom(true);
    try {
      await apiRequest(`/api/community/discussion-rooms/${roomId}/posts`, {
        method: 'POST',
        body: JSON.stringify({ type: 'discussion', title: newPostTitle.trim(), text: newPostText.trim() }),
      }, token);
      setNewPostTitle('');
      setNewPostText('');
      showSuccessToast('Posted.');
      invalidateCommunityCache('/api/community/discussion-rooms');
      await refreshDiscussionFeed(true);
    } catch (error) {
      handleApiError(error, 'Could not post.');
    } finally {
      setIsPostingRoom(false);
    }
  };

  const addAnswer = async (postId: string) => {
    if (!token || replyingPostId) return;
    const text = String(answerTextByPostId[postId] || '').trim();
    if (!text) return;
    setReplyingPostId(postId);
    try {
      await apiRequest(`/api/community/discussion-posts/${postId}/answers`, {
        method: 'POST',
        body: JSON.stringify({ text }),
      }, token);
      setAnswerTextByPostId((prev) => ({ ...prev, [postId]: '' }));
      invalidateCommunityCache('/api/community/discussion');
      await refreshDiscussionFeed(true);
    } catch (error) {
      handleApiError(error, 'Could not post answer.');
    } finally {
      setReplyingPostId(null);
    }
  };

  const upvoteDiscussion = async (postId: string, answerId?: string) => {
    if (!token) return;
    const key = answerId ? `${postId}:${answerId}` : postId;
    if (upvotingKey) return;
    setUpvotingKey(key);
    try {
      await apiRequest(`/api/community/discussion-posts/${postId}/upvote`, {
        method: 'POST',
        body: JSON.stringify({ targetType: answerId ? 'answer' : 'post', answerId: answerId || '' }),
      }, token);
      invalidateCommunityCache('/api/community/discussion');
      await refreshDiscussionFeed(true);
    } catch (error) {
      handleApiError(error, 'Could not update vote.');
    } finally {
      setUpvotingKey(null);
    }
  };

  const sendCommunityMessage = async (payload: {
    messageType: 'text' | 'file' | 'voice' | 'call-invite';
    text?: string;
    attachment?: MessageAttachment | null;
    voiceMeta?: { durationSeconds: number };
    callInvite?: { mode: 'audio' | 'video'; roomUrl: string; roomCode: string };
  }) => {
    if (!token || !activeConnectionId || isSendingMessage) return;
    if (activeConnection && activeConnection.canMessage === false) {
      showErrorToast('Messaging is blocked for this connection until unblocked.');
      return;
    }

    if (typingIdleTimerRef.current) window.clearTimeout(typingIdleTimerRef.current);
    if (typingEmitTimerRef.current) window.clearTimeout(typingEmitTimerRef.current);
    void postTyping(false);

    const fingerprint = `${payload.messageType}:${payload.text || ''}:${payload.attachment?.name || ''}:${Date.now()}`;
    const coarseKey = `${payload.messageType}:${payload.text || ''}:${payload.attachment?.name || ''}`;
    if (lastSendFingerprintRef.current === coarseKey) {
      showInfoToast('Message already sending or duplicate.');
      return;
    }
    lastSendFingerprintRef.current = coarseKey;
    window.setTimeout(() => {
      if (lastSendFingerprintRef.current === coarseKey) {
        lastSendFingerprintRef.current = '';
      }
    }, 2500);

    const sendOnce = async () => apiRequest<{ message: MessageRow }>(`/api/community/messages/${activeConnectionId}`, {
      method: 'POST',
      body: JSON.stringify(payload),
    }, token);

    try {
      setIsSendingMessage(true);
      let created: { message: MessageRow } | null = null;
      try {
        created = await sendOnce();
      } catch (firstError) {
        await new Promise((r) => setTimeout(r, 400));
        try {
          created = await sendOnce();
        } catch {
          throw firstError;
        }
      }
      if (created?.message?.id) {
        setMessages((prev) => upsertCommunityMessage(prev, created.message));
      }
      showSuccessToast('Message sent.');
      setMessageInput('');
      setMessageAttachment(null);
      invalidateCommunityCache('/api/community/messages');
      await refreshActiveMessages();
    } catch (error) {
      handleApiError(error, 'Could not send message.');
      showErrorToast('Delivery failed. You can try again.');
    } finally {
      setIsSendingMessage(false);
    }
  };

  const sendMessage = async () => {
    const text = messageInput.trim();
    if (!text) return;
    await sendCommunityMessage({ messageType: 'text', text });
  };

  const sendFileMessage = async () => {
    if (!messageAttachment) {
      showErrorToast('Select a file first.');
      return;
    }
    await sendCommunityMessage({ messageType: 'file', attachment: messageAttachment });
  };

  const onMessageFileSelected = async (event: ChangeEvent<HTMLInputElement>) => {
    const selected = event.target.files?.[0] || null;
    if (!selected) return;

    if (!isAllowedCommunityChatFile(selected)) {
      showErrorToast('Unsupported file type. Attach a PDF or an image (JPG, JPEG, PNG, or WEBP).');
      event.currentTarget.value = '';
      return;
    }

    if (selected.size > CHAT_ATTACHMENT_MAX_BYTES) {
      showErrorToast('File is too large. Attachments must be 10MB or smaller.');
      event.currentTarget.value = '';
      return;
    }

    try {
      const dataUrl = await fileToDataUrl(selected);
      const mimeType = String(selected.type || '').toLowerCase()
        || (communityFileExtension(selected.name) === '.pdf' ? 'application/pdf' : 'application/octet-stream');
      setMessageAttachment({
        name: selected.name,
        mimeType,
        size: selected.size,
        dataUrl,
      });
      showSuccessToast('File attached to chat.');
    } catch {
      showErrorToast('Could not read selected file.');
    } finally {
      event.currentTarget.value = '';
    }
  };

  const toggleMessageReaction = async (messageId: string, emoji: string) => {
    if (!token || !emoji) return;
    try {
      await apiRequest(`/api/community/messages/${messageId}/reactions`, {
        method: 'POST',
        body: JSON.stringify({ emoji }),
      }, token);
      invalidateCommunityCache('/api/community/messages');
      await refreshActiveMessages();
    } catch (error) {
      handleApiError(error, 'Could not update reaction.');
    }
  };

  const toggleBlockConnection = async () => {
    if (!token || !activeConnection) return;
    const nextBlockedState = !activeConnection.blockedByMe;
    try {
      setIsBlockingConnection(true);
      await apiRequest(`/api/community/connections/${activeConnection.connectionId}/block`, {
        method: 'POST',
        body: JSON.stringify({ blocked: nextBlockedState }),
      }, token);
      showSuccessToast(nextBlockedState ? 'Connection blocked. Messaging paused.' : 'Connection unblocked. Messaging restored.');
      invalidateCommunityCache('/api/community/connections');
      await refreshCommunity(true);
    } catch (error) {
      handleApiError(error, 'Could not update block state.');
    } finally {
      setIsBlockingConnection(false);
    }
  };

  const unfriendConnection = async () => {
    if (!token || !activeConnection) return;
    const approved = window.confirm('Unfriend this connection? This will remove your shared chat history.');
    if (!approved) return;
    try {
      setIsUnfriendingConnection(true);
      await apiRequest(`/api/community/connections/${activeConnection.connectionId}/unfriend`, {
        method: 'POST',
      }, token);
      showSuccessToast('Connection removed.');
      setActiveConnectionId('');
      setMessages([]);
      invalidateCommunityCache('/api/community/connections');
      await refreshCommunity(true);
    } catch (error) {
      handleApiError(error, 'Could not unfriend this connection.');
    } finally {
      setIsUnfriendingConnection(false);
    }
  };

  const sendCallInvite = async (mode: 'audio' | 'video') => {
    if (!activeConnection) return;
    const { roomCode, roomUrl } = createCallRoomUrl(activeConnection.connectionId);
    await sendCommunityMessage({
      messageType: 'call-invite',
      text: `${mode === 'audio' ? 'Audio' : 'Video'} call invitation`,
      callInvite: { mode, roomCode, roomUrl },
    });
  };


  const startVoiceRecording = async () => {
    if (!navigator.mediaDevices?.getUserMedia || !window.MediaRecorder) {
      showErrorToast('Voice notes are not supported on this device/browser.');
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream);
      const chunks: BlobPart[] = [];

      recorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) chunks.push(event.data);
      };

      recorder.onstop = async () => {
        const blob = new Blob(chunks, { type: recorder.mimeType || 'audio/webm' });
        const file = new File([blob], `voice-note-${Date.now()}.webm`, { type: blob.type || 'audio/webm' });
        const dataUrl = await fileToDataUrl(file);
        const durationSeconds = Math.max(1, Math.floor((Date.now() - voiceStartAtRef.current) / 1000));
        await sendCommunityMessage({
          messageType: 'voice',
          attachment: {
            name: file.name,
            mimeType: file.type || 'audio/webm',
            size: file.size,
            dataUrl,
          },
          voiceMeta: { durationSeconds },
        });

        stream.getTracks().forEach((track) => track.stop());
        voiceRecorderRef.current = null;
        voiceStreamRef.current = null;
        setIsRecordingVoice(false);
      };

      voiceRecorderRef.current = recorder;
      voiceStreamRef.current = stream;
      voiceStartAtRef.current = Date.now();
      recorder.start();
      setIsRecordingVoice(true);
      showNeutralToast('Recording voice note…');
    } catch {
      showErrorToast('Microphone permission denied or unavailable.');
    }
  };

  const stopVoiceRecording = () => {
    const recorder = voiceRecorderRef.current;
    if (!recorder) return;
    recorder.stop();
  };

  const reportConversation = async () => {
    if (!token || !activeConnection || !reportReason.trim()) return;
    try {
      await apiRequest('/api/community/report', {
        method: 'POST',
        body: JSON.stringify({
          connectionId: activeConnection.connectionId,
          reportedUserId: activeConnection.user.id,
          reason: reportReason.trim(),
        }),
      }, token);
      setReportReason('');
      showSuccessToast('Report submitted. Our moderation team will review this.');
    } catch (error) {
      handleApiError(error, 'Could not submit report.');
    }
  };

  const createQuizChallenge = async () => {
    if (!token) return;
    if (!quizOpponentUserId) {
      showErrorToast('Select a student to challenge.');
      return;
    }
    try {
      await apiRequest('/api/community/quiz-challenges', {
        method: 'POST',
        body: JSON.stringify({
          opponentUserId: quizOpponentUserId,
          challengeType: quizChallengeType,
          mode: quizMode,
          subject: quizSubject,
          topic: quizTopic,
          difficulty: quizDifficulty,
          questionCount: quizQuestionCount,
          durationSeconds: quizDurationSeconds,
        }),
      }, token);
      showSuccessToast('Quiz challenge sent.');
      setQuizAnswers({});
      setQuizStartedAtMs(null);
      invalidateCommunityCache('/api/community/quiz');
      await loadQuizData(true);
    } catch (error) {
      handleApiError(error, 'Could not create quiz challenge.');
    }
  };

  const respondQuizChallenge = async (challengeId: string, action: 'accept' | 'decline') => {
    if (!token) return;
    try {
      const payload = await apiRequest<{ challenge: QuizChallengeRow }>(`/api/community/quiz-challenges/${challengeId}/respond`, {
        method: 'POST',
        body: JSON.stringify({ action }),
      }, token);
      if (action === 'accept') {
        openChallengeExamWindow(challengeId);
      }
      showSuccessToast(action === 'accept' ? 'Challenge accepted.' : 'Challenge declined.');
      invalidateCommunityCache('/api/community/quiz');
      await loadQuizData(true);
    } catch (error) {
      handleApiError(error, 'Could not respond to challenge.');
    }
  };

  const openChallengeExamWindow = (challengeId: string) => {
    if (!token) {
      showErrorToast('Please login first to launch a challenge test.');
      return;
    }

    const urlAuth = bearerForLaunchUrl(token);
    localStorage.setItem('net360-exam-launch', JSON.stringify({
      testType: 'challenge',
      challengeId,
      ...(urlAuth ? { authToken: urlAuth } : {}),
      launchedAt: Date.now(),
    }));

    const url = urlAuth
      ? `/exam-interface?testType=challenge&challengeId=${encodeURIComponent(challengeId)}&authToken=${encodeURIComponent(urlAuth)}`
      : `/exam-interface?testType=challenge&challengeId=${encodeURIComponent(challengeId)}`;
    navigateToExamSameTab(url);
  };

  const startChallengeAttempt = (challengeId: string) => {
    setSelectedQuizChallengeId(challengeId);
    openChallengeExamWindow(challengeId);
  };

  const submitQuizChallenge = async () => {
    if (!token || !selectedQuizChallenge) return;
    const answers = selectedQuizChallenge.questions.map((question) => ({
      questionId: question.questionId,
      selectedOption: String(quizAnswers[question.questionId] || '').trim(),
    }));
    const elapsedSeconds = quizStartedAtMs ? Math.max(0, Math.floor((Date.now() - quizStartedAtMs) / 1000)) : 0;

    try {
      await apiRequest(`/api/community/quiz-challenges/${selectedQuizChallenge.id}/submit`, {
        method: 'POST',
        body: JSON.stringify({ answers, elapsedSeconds }),
      }, token);
      showSuccessToast('Challenge submitted.');
      setQuizStartedAtMs(null);
      setQuizAnswers({});
      invalidateCommunityCache('/api/community/quiz');
      await loadQuizData(true);
    } catch (error) {
      handleApiError(error, 'Could not submit challenge.');
    }
  };

  const profileCompletionPercent = useMemo(() => {
    let score = 0;
    if (usernameInput.trim()) score += 22;
    if (bio.trim()) score += 18;
    if (targetNetType) score += 10;
    if (subjectsNeedHelpInput.trim()) score += 15;
    if (Number(scoreRangeMax) > 0) score += 8;
    if (profilePictureDataUrl || profile?.profilePictureUrl) score += 12;
    if (interestsInput.trim()) score += 15;
    return Math.min(100, score);
  }, [usernameInput, bio, targetNetType, subjectsNeedHelpInput, scoreRangeMax, profilePictureDataUrl, profile?.profilePictureUrl, interestsInput]);

  // Roster tile actions navigate within the page. The pressed state is set synchronously on
  // click (so the tap is acknowledged) and cleared by the effect below once the target tab has
  // actually rendered — not by a timer.
  const beginRosterAction = (userId: string, kind: RosterActionKind) => {
    if (pendingRosterAction) return false;
    setPendingRosterAction({ userId, kind });
    return true;
  };

  useEffect(() => {
    if (!pendingRosterAction) return;
    if (activeTab === ROSTER_ACTION_TARGET_TAB[pendingRosterAction.kind]) {
      setPendingRosterAction(null);
    }
  }, [activeTab, pendingRosterAction]);

  const openChatWithUser = (userId: string) => {
    if (!beginRosterAction(userId, 'chat')) return;
    const row = connections.find((c) => c.user.id === userId);
    if (row) {
      setActiveConnectionId(row.connectionId);
      setActiveTab('messages');
      showInfoToast('Messages');
      return;
    }
    // Nothing to open: release the pressed state immediately and explain.
    setPendingRosterAction(null);
    showInfoToast('Connect to start chatting.');
  };

  const inviteToQuizBattleFromPresence = (userId: string) => {
    if (!beginRosterAction(userId, 'quiz')) return;
    setQuizOpponentUserId(userId);
    setActiveTab('quiz-battles');
    showInfoToast('Quiz Battles');
  };

  const openStudyRoomFromPresence = (userId: string) => {
    if (!beginRosterAction(userId, 'room')) return;
    if (rooms[0]?.id) setActiveRoomId(rooms[0].id);
    setActiveTab('discussion-rooms');
  };

  const downloadBadgeCertificate = async (badge: BadgeRow) => {
    if (!token || !badge.earned || isDownloadingCertificate) return;
    setIsDownloadingCertificate(true);
    try {
      const { blob, filename } = await downloadBinary(`/api/community/achievements/${encodeURIComponent(badge.id)}/certificate`, {}, token);
      await openOrSaveBlobOnDevice(blob, filename || `NET360-${badge.id}-certificate.pdf`, 'download');
      showSuccessToast('Certificate downloaded.');
    } catch (error) {
      handleApiError(error, 'Could not download the certificate.');
    } finally {
      setIsDownloadingCertificate(false);
    }
  };

  if (!token || !user) {
    return (
      <Card>
        <CardHeader>
          <CardTitle as="h2">Community</CardTitle>
          <CardDescription>Please sign in to access community features.</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  if (subLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle as="h2">Community</CardTitle>
          <CardDescription>Loading subscription…</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  if (!communityAccessAllowed) {
    return (
      <div className="space-y-4">
        <div className="flex justify-end">
          <PremiumCountdownBadge />
        </div>
        <PremiumLockScreen
          title="Unlock community"
          description={isNativeRuntime()
            ? 'Connect with students, join quiz battles, and use discussion rooms with an active trial or premium subscription.'
            : 'Subscribe to Community to connect with students, join quiz battles, and use discussion rooms.'}
        />
      </div>
    );
  }

  const threadPost = feedPosts.find((post) => post.id === openThreadId) || null;
  const sectionTabTriggerClassName =
    'shrink-0 whitespace-nowrap rounded-xl border border-slate-200/90 bg-white/90 px-4 py-2.5 text-sm font-semibold text-slate-700 transition-all duration-300 ease-out hover:-translate-y-0.5 hover:scale-[1.02] hover:border-cyan-300 hover:bg-cyan-50 hover:text-cyan-700 hover:shadow-[0_10px_20px_rgba(34,211,238,0.2)] active:scale-[0.98] data-[state=active]:!border-transparent data-[state=active]:!bg-gradient-to-r data-[state=active]:!from-indigo-600 data-[state=active]:!via-violet-500 data-[state=active]:!to-fuchsia-500 data-[state=active]:!text-white data-[state=active]:shadow-[0_14px_26px_rgba(109,40,217,0.34)]';
  const viewProfileButtonClassName =
    'border-cyan-300 bg-gradient-to-r from-white to-cyan-50 text-cyan-700 transition-all duration-250 ease-out hover:-translate-y-0.5 hover:scale-[1.03] hover:border-cyan-400 hover:from-cyan-50 hover:to-blue-50 hover:text-cyan-800 hover:shadow-[0_10px_16px_rgba(34,211,238,0.22)] active:scale-[0.98]';

  return (
    <div className="community-page min-w-0 space-y-4">
      <h1 className="sr-only">Community — students, discussions, quiz battles, and messages</h1>
      {demoCommunity ? (
        <DemoModeBanner message={`Community access ends in ${formatDemoTimeLeft(demo.communityMsLeft)}.`} />
      ) : null}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full space-y-4" aria-label="Community sections">
        <div className="net360-horizontal-scroll net360-swipe-row -mx-1 px-1 pb-1 [scrollbar-gutter:stable]">
          <TabsList className="inline-flex h-auto w-max min-w-max flex-nowrap gap-2 rounded-2xl border border-slate-200 bg-gradient-to-r from-sky-50 via-indigo-50 to-fuchsia-50 p-1.5 shadow-[0_10px_20px_rgba(99,102,241,0.12)]">
            <TabsTrigger value="online-students" className={sectionTabTriggerClassName}>Online</TabsTrigger>
            <TabsTrigger value="discover-students" className={sectionTabTriggerClassName}>Discover Students</TabsTrigger>
            <TabsTrigger value="study-partners" className={sectionTabTriggerClassName}>Study Partners</TabsTrigger>
            <TabsTrigger value="discussion-rooms" className={sectionTabTriggerClassName}>Discussion Rooms</TabsTrigger>
            <TabsTrigger value="quiz-battles" className={sectionTabTriggerClassName}>Quiz Battles</TabsTrigger>
            <TabsTrigger value="leaderboard" className={sectionTabTriggerClassName}>Leaderboard</TabsTrigger>
            <TabsTrigger value="messages" className={sectionTabTriggerClassName}>Messages</TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value="online-students" className="mt-0 space-y-4">
          <Card className="overflow-hidden border-indigo-200/60 bg-gradient-to-br from-white via-indigo-50/30 to-fuchsia-50/20 shadow-md dark:border-indigo-500/20 dark:from-slate-950 dark:via-indigo-950/20 dark:to-slate-900">
            <CardHeader>
              <CardTitle className="flex flex-wrap items-center gap-2 text-balance">
                Online students
                {realtimeDown ? (
                  <Badge variant="outline" className="border-amber-300 text-amber-800 dark:text-amber-200" aria-live="polite">Connecting…</Badge>
                ) : (
                  <Badge className="bg-emerald-600 text-white shadow-sm">Live</Badge>
                )}
              </CardTitle>
              <CardDescription>Students currently online.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {!presenceResolved && otherOnlineStudents.length === 0 ? (
                <div className="flex justify-center py-8" role="status" aria-live="polite">
                  <span className="inline-block h-6 w-6 animate-spin rounded-full border-2 border-indigo-200 border-t-indigo-600 dark:border-indigo-900 dark:border-t-indigo-300" />
                  <span className="sr-only">Loading online students</span>
                </div>
              ) : null}

              <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
                {otherOnlineStudents.map((s) => {
                  const netStatus = s.connectionStatus
                    || [...allCommunityUsers, ...searchResults].find((x) => x.id === s.id)?.connectionStatus;
                  const pendingKind = pendingRosterAction?.userId === s.id ? pendingRosterAction.kind : null;
                  const isConnecting = connectingUserIds.has(s.id);
                  const tileName = displayName(s);
                  return (
                    <div
                      key={s.id}
                      className="group relative overflow-hidden rounded-2xl border border-slate-200/90 bg-white/95 p-4 shadow-sm ring-1 ring-transparent transition duration-300 hover:-translate-y-1 hover:shadow-lg hover:ring-indigo-200/60 dark:border-slate-700 dark:bg-slate-900/75 dark:hover:ring-indigo-500/30"
                    >
                      <div className="absolute right-3 top-3 flex items-center gap-1.5">
                        <span
                          className={`inline-flex h-2.5 w-2.5 rounded-full shadow-[0_0_8px_currentColor] ${s.presenceStatus === 'away' ? 'bg-amber-400 text-amber-400' : 'bg-emerald-500 text-emerald-500'} animate-pulse`}
                        />
                        <span className="text-[10px] font-bold uppercase tracking-wide text-muted-foreground">
                          {s.presenceStatus === 'away' ? 'Away' : 'Online'}
                        </span>
                      </div>
                      <div className="flex items-center gap-3 pr-12">
                        <CommunityAvatar userLike={s} sizeClass="h-12 w-12 ring-2 ring-white shadow-md dark:ring-slate-800" />
                        <div className="min-w-0">
                          <p className="flex min-w-0 items-center gap-1.5 font-semibold">
                            <span className="truncate">{tileName}</span>
                          </p>
                          <p className="truncate text-xs text-muted-foreground">{s.username ? `@${s.username}` : 'Student'}</p>
                          <p className="truncate text-xs font-medium text-indigo-700 dark:text-indigo-300">
                            {(s.targetNetType || 'net-engineering').replace(/-/g, ' ')}
                          </p>
                        </div>
                      </div>
                      {s.activity ? (
                        <p className="mt-3 text-xs font-medium text-slate-700 dark:text-slate-200">
                          {s.activity}
                        </p>
                      ) : null}
                      {s.lastSeenAt ? (
                        <p className="mt-1 text-[10px] text-muted-foreground">Last seen {new Date(s.lastSeenAt).toLocaleString()}</p>
                      ) : null}
                      <div className="mt-4 flex flex-wrap gap-2">
                        <Button
                          type="button"
                          size="sm"
                          variant="secondary"
                          className="shadow-sm"
                          onClick={() => openChatWithUser(s.id)}
                          disabled={netStatus !== 'connected' || Boolean(pendingRosterAction)}
                          aria-busy={pendingKind === 'chat'}
                          aria-label={netStatus !== 'connected' ? `Chat locked until connected with ${tileName}` : pendingKind === 'chat' ? `Opening chat with ${tileName}` : `Chat with ${tileName}`}
                        >
                          {pendingKind === 'chat' ? 'Opening…' : 'Chat'}
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            if (netStatus === 'pending-received') {
                              setActiveTab('discover-students');
                              return;
                            }
                            void sendConnectionRequest(s.id);
                          }}
                          disabled={isConnecting || (netStatus !== 'pending-received' && !canSendConnectionRequest(netStatus))}
                          aria-busy={isConnecting}
                          aria-label={`${connectButtonLabel(netStatus, s.id)} — ${tileName}`}
                        >
                          {connectButtonLabel(netStatus, s.id)}
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          onClick={() => inviteToQuizBattleFromPresence(s.id)}
                          disabled={Boolean(pendingRosterAction)}
                          aria-busy={pendingKind === 'quiz'}
                          aria-label={pendingKind === 'quiz' ? `Starting quiz battle with ${tileName}` : `Quiz battle with ${tileName}`}
                        >
                          {pendingKind === 'quiz' ? 'Starting…' : 'Quiz battle'}
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          className="text-indigo-700 dark:text-indigo-300"
                          onClick={() => openStudyRoomFromPresence(s.id)}
                          disabled={Boolean(pendingRosterAction)}
                          aria-busy={pendingKind === 'room'}
                          aria-label={pendingKind === 'room' ? 'Opening study room' : 'Open study room'}
                        >
                          {pendingKind === 'room' ? 'Opening…' : 'Study room'}
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
              {presenceResolved && otherOnlineStudents.length === 0 && !presenceLoading ? (
                <div className="rounded-xl border border-dashed p-8 text-center">
                  <p className="text-sm font-medium text-slate-800 dark:text-slate-100">No other students are currently online.</p>
                </div>
              ) : null}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="discover-students" className="mt-0 space-y-4">
          <Card className="min-h-[120px]">
            <CardHeader>
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div>
                  <CardTitle>Community Profile</CardTitle>
                  <CardDescription>Your community profile.</CardDescription>
                  <div className="mt-3 space-y-1">
                    <div className="flex items-center justify-between text-xs text-muted-foreground">
                      <span>Profile strength</span>
                      <span>{profileCompletionPercent}%</span>
                    </div>
                    <div className="h-2 overflow-hidden rounded-full bg-slate-200/80 dark:bg-slate-800">
                      <div
                        className="h-full rounded-full bg-gradient-to-r from-cyan-500 via-indigo-500 to-violet-500 transition-all duration-500 ease-out"
                        style={{ width: `${profileCompletionPercent}%` }}
                      />
                    </div>
                  </div>
                </div>
                {!isCommunityProfileExpanded ? (
                  <Button type="button" variant="outline" onClick={() => setIsCommunityProfileExpanded(true)}>
                    Edit Profile
                  </Button>
                ) : null}
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              {!isCommunityProfileExpanded ? (
                <div className="grid gap-2 rounded-lg border bg-slate-50/70 p-3 text-sm md:grid-cols-2">
                  <p><span className="text-muted-foreground">Username:</span> {usernameInput || 'Not set'}</p>
                  <p><span className="text-muted-foreground">Target NET:</span> {targetNetType || 'Not set'}</p>
                  <p><span className="text-muted-foreground">Help Subjects:</span> {subjectsNeedHelpInput || 'Not set'}</p>
                  <p><span className="text-muted-foreground">Preparation:</span> {preparationLevel}</p>
                  <p><span className="text-muted-foreground">Study Time:</span> {studyTimePreference}</p>
                  <p><span className="text-muted-foreground">Score Range:</span> {scoreRangeMin} - {scoreRangeMax}</p>
                  <p className="md:col-span-2"><span className="text-muted-foreground">Bio:</span> {bio || 'Not set'}</p>
                  <p className="md:col-span-2"><span className="text-muted-foreground">Profile Picture:</span> {hasCommunityProfileData && profilePictureDataUrl ? 'Added' : 'Not set'}</p>
                </div>
              ) : null}

              {isCommunityProfileExpanded ? (
                <>
              <div className="grid gap-3 md:grid-cols-2">
                <div className="space-y-1.5">
                  <Label>Community Username</Label>
                  <Input value={usernameInput} onChange={(e) => setUsernameInput(e.target.value)} placeholder="e.g. future-engineer" />
                </div>
                <div className="space-y-1.5">
                  <Label>Profile Picture Upload (optional)</Label>
                  <Input
                    type="file"
                    accept=".jpg,.jpeg,.png,.webp,.gif,.svg,image/jpeg,image/png,image/webp,image/gif,image/svg+xml"
                    onChange={(e) => void onProfilePictureSelected(e)}
                  />
                  {profilePictureUploadName ? <p className="text-xs text-muted-foreground">Selected: {profilePictureUploadName}</p> : null}
                  {profilePictureDataUrl ? (
                    <div className="flex items-center gap-3 rounded-md border p-2">
                      <img
                        src={getMediaUrl(profilePictureDataUrl)}
                        alt="Profile preview"
                        width={48}
                        height={48}
                        className="h-12 w-12 rounded-full border object-cover"
                        loading="lazy"
                        decoding="async"
                      />
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setProfilePictureDataUrl('');
                          setProfilePictureUploadName('');
                        }}
                      >
                        Remove Picture
                      </Button>
                    </div>
                  ) : null}
                </div>
              </div>

              <div className="grid gap-3 md:grid-cols-2">
                <div className="space-y-1.5">
                  <Label>Target NET Type</Label>
                  <Select value={targetNetType} onValueChange={setTargetNetType}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="net-engineering">NET Engineering</SelectItem>
                      <SelectItem value="net-applied-sciences">NET Applied Sciences</SelectItem>
                      <SelectItem value="net-business-social-sciences">NET Business & Social Sciences</SelectItem>
                      <SelectItem value="net-architecture">NET Architecture</SelectItem>
                      <SelectItem value="net-natural-sciences">NET Natural Sciences</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>Subjects You Need Help In</Label>
                  <Input value={subjectsNeedHelpInput} onChange={(e) => setSubjectsNeedHelpInput(e.target.value)} placeholder="physics, mathematics" />
                </div>
              </div>

              <div className="grid gap-3 md:grid-cols-3">
                <div className="space-y-1.5">
                  <Label>Preparation Level</Label>
                  <Select value={preparationLevel} onValueChange={(v) => setPreparationLevel(v as 'beginner' | 'intermediate' | 'advanced')}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="beginner">Beginner</SelectItem>
                      <SelectItem value="intermediate">Intermediate</SelectItem>
                      <SelectItem value="advanced">Advanced</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>Study Time Preference</Label>
                  <Select value={studyTimePreference} onValueChange={(v) => setStudyTimePreference(v as 'morning' | 'evening' | 'night' | 'flexible')}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="morning">Morning</SelectItem>
                      <SelectItem value="evening">Evening</SelectItem>
                      <SelectItem value="night">Night</SelectItem>
                      <SelectItem value="flexible">Flexible</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>Score Range</Label>
                  <div className="grid grid-cols-2 gap-2">
                    <Input type="number" min={0} max={200} value={scoreRangeMin} onChange={(e) => setScoreRangeMin(Number(e.target.value || 0))} />
                    <Input type="number" min={0} max={200} value={scoreRangeMax} onChange={(e) => setScoreRangeMax(Number(e.target.value || 200))} />
                  </div>
                </div>
              </div>

              <div className="space-y-1.5">
                <Label>Short Profile Bio</Label>
                <Textarea value={bio} onChange={(e) => setBio(e.target.value)} className="min-h-[70px]" />
              </div>

              <div className="space-y-1.5">
                <Label>Interests</Label>
                <Input
                  value={interestsInput}
                  onChange={(e) => setInterestsInput(e.target.value)}
                  placeholder="calculus, group study, late-night sessions"
                />
              </div>

              <div className="grid gap-3 sm:grid-cols-2">
                <div className="flex items-center gap-3 rounded-lg border border-slate-200 bg-white p-3 dark:border-slate-600 dark:bg-slate-800">
                  <Switch checked={hideOnlineStatus} onCheckedChange={setHideOnlineStatus} id="hide-online" />
                  <Label htmlFor="hide-online" className="cursor-pointer text-sm leading-snug">
                    Hide my online status
                  </Label>
                </div>
                <div className="flex items-center gap-3 rounded-lg border border-slate-200 bg-white p-3 dark:border-slate-600 dark:bg-slate-800">
                  <Switch checked={doNotDisturb} onCheckedChange={setDoNotDisturb} id="dnd-mode" />
                  <Label htmlFor="dnd-mode" className="cursor-pointer text-sm leading-snug">
                    Do not disturb
                  </Label>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <Switch checked={shareProfilePicture} onCheckedChange={setShareProfilePicture} />
                <p className="text-sm text-muted-foreground">Allow other students to view my profile picture</p>
              </div>

              <div className="flex flex-wrap gap-2">
                <Button onClick={() => void saveCommunityProfile()} disabled={isSavingCommunityProfile}>
                  {isSavingCommunityProfile ? 'Saving…' : 'Save Community Profile'}
                </Button>
                {hasCommunityProfileData ? (
                  <Button type="button" variant="outline" onClick={() => setIsCommunityProfileExpanded(false)}>
                    Cancel
                  </Button>
                ) : null}
              </div>
                </>
              ) : null}
            </CardContent>
          </Card>

          <div className="grid gap-4 xl:grid-cols-3">
            <Card className="min-h-[120px] min-w-0 xl:col-span-1">
              <CardHeader>
                <CardTitle>Find Students</CardTitle>
                <CardDescription>Search for students.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex flex-col gap-2 sm:flex-row">
                  <Input
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search by name, username, email, city"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        void searchUsers(searchQuery, true);
                      }
                    }}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full sm:w-auto"
                    disabled={searchLoading || !searchQuery.trim()}
                    onClick={() => void searchUsers(searchQuery, true)}
                  >
                    Search
                  </Button>
                </div>
                <div className="space-y-2 max-h-[320px] overflow-auto">
                  {searchResults.map((result) => (
                    <div key={result.id} className="rounded-lg border p-3">
                      <div className="flex items-start gap-2">
                        <CommunityAvatar userLike={result} />
                        <div>
                          <p className="text-sm">{displayName(result)}</p>
                          <p className="text-xs text-muted-foreground">{result.targetProgram || 'Program not set'}{result.city ? `  ${result.city}` : ''}</p>
                        </div>
                      </div>
                      <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
                        <Badge variant="outline">{result.connectionStatus || 'none'}</Badge>
                        <div className="flex w-full flex-wrap gap-1 sm:w-auto">
                          {canSendConnectionRequest(result.connectionStatus) ? (
                            <Button
                              size="sm"
                              className="w-full sm:w-auto"
                              onClick={() => void sendConnectionRequest(result.id)}
                              disabled={connectingUserIds.has(result.id)}
                              aria-busy={connectingUserIds.has(result.id)}
                            >
                              {connectingUserIds.has(result.id) ? 'Connecting…' : 'Connect'}
                            </Button>
                          ) : (
                            <Button size="sm" variant="secondary" className="w-full sm:w-auto" disabled>
                              {result.connectionStatus === 'connected'
                                ? 'Connected'
                                : result.connectionStatus === 'pending-received'
                                  ? 'Request Received'
                                  : 'Request Pending'}
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="outline"
                            className={`${viewProfileButtonClassName} w-full sm:w-auto`}
                            onClick={() => setProfilePreview(result)}
                          >
                            View Profile
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                  {!discoverSearched ? (
                    <p className="text-xs text-muted-foreground">Enter a name or letter, then click Search.</p>
                  ) : !searchLoading && !searchResults.length ? (
                    <p className="text-xs text-muted-foreground">No students found.</p>
                  ) : null}
                </div>
              </CardContent>
            </Card>

            <Card className="min-w-0 xl:col-span-2">
              <CardHeader>
                <CardTitle>Connection Requests</CardTitle>
                <CardDescription>Incoming and outgoing requests.</CardDescription>
              </CardHeader>
              <CardContent className="grid gap-3 md:grid-cols-2">
                <div className="space-y-2">
                  <p className="text-sm">Incoming</p>
                  <div className="space-y-2 max-h-[220px] overflow-auto">
                    {incomingRequests.map((item) => (
                      <div key={item.id} className="rounded-lg border p-3 space-y-2">
                        <div className="flex items-center gap-2">
                          <CommunityAvatar userLike={item.user} />
                          <p className="text-sm">{displayName(item.user)}</p>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          <Button size="sm" className="w-full sm:w-auto" onClick={() => void respondToRequest(item.id, 'accept')}>Accept</Button>
                          <Button size="sm" variant="outline" className="w-full sm:w-auto" onClick={() => void respondToRequest(item.id, 'reject')}>Reject</Button>
                        </div>
                      </div>
                    ))}
                    {!incomingRequests.length ? <p className="text-xs text-muted-foreground">No incoming requests.</p> : null}
                  </div>
                </div>
                <div className="space-y-2">
                  <p className="text-sm">Outgoing</p>
                  <div className="space-y-2 max-h-[220px] overflow-auto">
                    {outgoingRequests.map((item) => (
                      <div key={item.id} className="rounded-lg border p-3">
                        <div className="flex items-center gap-2">
                          <CommunityAvatar userLike={item.user} />
                          <p className="text-sm">{displayName(item.user)}</p>
                        </div>
                        <Badge variant="outline" className="mt-2">Pending</Badge>
                      </div>
                    ))}
                    {!outgoingRequests.length ? <p className="text-xs text-muted-foreground">No outgoing requests.</p> : null}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {profilePreview ? (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CommunityAvatar userLike={profilePreview} />
                  <span>{displayName(profilePreview)}</span>
                </CardTitle>
                <CardDescription>Profile preview</CardDescription>
              </CardHeader>
              <CardContent className="space-y-1 text-sm text-muted-foreground">
                <p>Target NET: {profilePreview.targetNetType || '-'}</p>
                <p>Needs Help: {(profilePreview.subjectsNeedHelp || []).join(', ') || '-'}</p>
                <p>Level: {profilePreview.preparationLevel || '-'}</p>
                <p>Time: {profilePreview.studyTimePreference || '-'}</p>
                <p>Score: {Math.round(Number(profilePreview.score || 0))}</p>
                <Button size="sm" variant="outline" onClick={() => setProfilePreview(null)}>Close</Button>
              </CardContent>
            </Card>
          ) : null}
        </TabsContent>

        <TabsContent value="study-partners" className="mt-0 space-y-4">
          <Card className="min-w-0">
            <CardHeader>
              <CardTitle>Smart Study Partner Matching</CardTitle>
              <CardDescription>Suggested study partners.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-2 max-h-[560px] overflow-auto">
              {studyPartners.map((item) => (
                <div key={item.user.id} className="rounded-lg border p-3">
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div className="flex items-start gap-2">
                      <CommunityAvatar userLike={item.user} />
                      <div>
                        <p className="text-sm font-medium">{displayName(item.user)}</p>
                        <p className="text-xs text-muted-foreground">{item.user.targetNetType || 'NET profile pending'}</p>
                        <p className="text-xs text-muted-foreground">Needs help: {(item.user.subjectsNeedHelp || []).join(', ') || 'Not set'}</p>
                        <p className="text-xs text-muted-foreground">Score: {Math.round(Number(item.user.score || 0))}  {item.user.studyTimePreference || 'flexible'} sessions</p>
                        {item.reasons?.length ? <p className="text-xs text-emerald-700 mt-1">{item.reasons.join(' | ')}</p> : null}
                      </div>
                    </div>
                        <Badge className="match-percentage">{Math.round(item.compatibility)}% match</Badge>
                  </div>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {canSendConnectionRequest(item.user.connectionStatus) ? (
                      <Button
                        size="sm"
                        className="w-full sm:w-auto"
                        onClick={() => void sendConnectionRequest(item.user.id)}
                        disabled={connectingUserIds.has(item.user.id)}
                        aria-busy={connectingUserIds.has(item.user.id)}
                      >
                        {connectingUserIds.has(item.user.id) ? 'Connecting…' : 'Connect'}
                      </Button>
                    ) : (
                      <Button size="sm" variant="secondary" className="w-full sm:w-auto" disabled>
                        {item.user.connectionStatus === 'connected'
                          ? 'Connected'
                          : item.user.connectionStatus === 'pending-received'
                            ? 'Request Received'
                            : 'Request Pending'}
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="outline"
                      className={`${viewProfileButtonClassName} w-full sm:w-auto`}
                      onClick={() => setStudyPartnersProfilePreview(item.user)}
                    >
                      View Profile
                    </Button>
                  </div>
                </div>
              ))}
              {!studyPartners.length ? <p className="text-xs text-muted-foreground">No study partners found yet.</p> : null}
            </CardContent>
          </Card>

          {studyPartnersProfilePreview ? (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CommunityAvatar userLike={studyPartnersProfilePreview} />
                  <span>{displayName(studyPartnersProfilePreview)}</span>
                </CardTitle>
                <CardDescription>Profile preview</CardDescription>
              </CardHeader>
              <CardContent className="space-y-1 text-sm text-muted-foreground">
                <p>Target NET: {studyPartnersProfilePreview.targetNetType || '-'}</p>
                <p>Needs Help: {(studyPartnersProfilePreview.subjectsNeedHelp || []).join(', ') || '-'}</p>
                <p>Level: {studyPartnersProfilePreview.preparationLevel || '-'}</p>
                <p>Time: {studyPartnersProfilePreview.studyTimePreference || '-'}</p>
                <p>Score: {Math.round(Number(studyPartnersProfilePreview.score || 0))}</p>
                <Button size="sm" variant="outline" onClick={() => setStudyPartnersProfilePreview(null)}>Close</Button>
              </CardContent>
            </Card>
          ) : null}
        </TabsContent>

        <TabsContent value="discussion-rooms" className="net360-rooms mt-0 space-y-4">
          {openThreadId && threadPost ? (
            <article className="net360-thread">
              <button type="button" className="net360-thread-back" onClick={() => setOpenThreadId('')}>
                Back to posts
              </button>
              <h2>{threadPost.title || 'Discussion'}</h2>
              <p className="net360-thread-body">{threadPost.text}</p>
              <p className="net360-thread-meta">
                {threadPost.author ? displayName(threadPost.author) : 'Unknown'}
                {threadPost.createdAt ? ` · ${new Date(threadPost.createdAt).toLocaleString()}` : ''}
                {` · ${threadPost.answers.length} ${threadPost.answers.length === 1 ? 'comment' : 'comments'}`}
              </p>
              <div className="net360-comments">
                {threadPost.answers.map((answer) => (
                  <div key={answer.id} className="net360-comment">
                    <p className="net360-comment-author">{answer.author ? displayName(answer.author) : 'Unknown'}</p>
                    <p>{answer.text}</p>
                  </div>
                ))}
                {!threadPost.answers.length ? <p className="net360-thread-meta">No comments yet.</p> : null}
              </div>
              <div className="net360-comment-box">
                <Input
                  value={answerTextByPostId[threadPost.id] || ''}
                  onChange={(event) => setAnswerTextByPostId((prev) => ({ ...prev, [threadPost.id]: event.target.value }))}
                  placeholder="Write a comment"
                />
                <Button type="button" disabled={replyingPostId === threadPost.id} onClick={() => void addAnswer(threadPost.id)}>
                  {replyingPostId === threadPost.id ? 'Posting…' : 'Comment'}
                </Button>
              </div>
            </article>
          ) : (
            <div className="net360-topic-feed">
              <div className="net360-topic-compose">
                <Input value={newPostTitle} onChange={(event) => setNewPostTitle(event.target.value)} placeholder="Topic" />
                <Textarea value={newPostText} onChange={(event) => setNewPostText(event.target.value)} className="min-h-[88px]" placeholder="Write your post" />
                <Button type="button" onClick={() => void publishTopic()} disabled={isPostingRoom || !rooms.length}>
                  {isPostingRoom ? 'Posting…' : 'Post'}
                </Button>
              </div>
              {feedPosts.map((post) => (
                <button
                  key={post.id}
                  type="button"
                  className="net360-topic-card"
                  onClick={() => setOpenThreadId(post.id)}
                >
                  <p className="net360-topic-title">{post.title || 'Discussion'}</p>
                  <p className="net360-topic-text">{post.text}</p>
                  <p className="net360-topic-meta">
                    {post.author ? displayName(post.author) : 'Unknown'}
                    {` · ${post.answers.length} ${post.answers.length === 1 ? 'comment' : 'comments'}`}
                  </p>
                </button>
              ))}
              {!feedPosts.length ? <p className="net360-thread-meta">No posts yet. Start a topic above.</p> : null}
            </div>
          )}
        </TabsContent>

        <TabsContent value="quiz-battles" className="mt-0 space-y-4">
          <div className="grid gap-4 xl:grid-cols-[320px_minmax(0,1fr)]">
            <Card className="min-w-0">
              <CardHeader>
                <CardTitle>Create Quiz Challenge</CardTitle>
                <CardDescription>Create a quiz challenge.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="space-y-1.5">
                  <Label>Challenge Opponent</Label>
                  <Select value={quizOpponentUserId} onValueChange={setQuizOpponentUserId}>
                    <SelectTrigger><SelectValue placeholder="Select student" /></SelectTrigger>
                    <SelectContent className="net360-opaque-select">
                      {allCommunityUsers.map((item) => (
                        <SelectItem key={item.id} value={item.id}>
                          {displayName(item)}
                          {item.connectionStatus === 'connected' ? ' (connected)' : ''}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-1.5">
                  <Label>Challenge Type</Label>
                  <Select value={quizChallengeType} onValueChange={(value) => setQuizChallengeType(value as 'async' | 'live')}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="async">Async (offline/deferred)</SelectItem>
                      <SelectItem value="live">Live (simultaneous)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid gap-2 md:grid-cols-2 xl:grid-cols-1">
                  <div className="space-y-1.5">
                    <Label>Mode</Label>
                    <Select value={quizMode} onValueChange={(value) => setQuizMode(value as 'subject-wise' | 'mock' | 'adaptive' | 'custom')}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="subject-wise">Subject-wise</SelectItem>
                        <SelectItem value="mock">Mock Test</SelectItem>
                        <SelectItem value="adaptive">Adaptive Weak Areas</SelectItem>
                        <SelectItem value="custom">Custom Filters</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-1.5">
                    <Label>Subject</Label>
                    <Select value={quizSubject} onValueChange={setQuizSubject}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="mathematics">Mathematics</SelectItem>
                        <SelectItem value="physics">Physics</SelectItem>
                        <SelectItem value="english">English</SelectItem>
                        <SelectItem value="biology">Biology</SelectItem>
                        <SelectItem value="chemistry">Chemistry</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-1.5">
                    <Label>Difficulty</Label>
                    <Select value={quizDifficulty} onValueChange={setQuizDifficulty}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Easy">Easy</SelectItem>
                        <SelectItem value="Medium">Medium</SelectItem>
                        <SelectItem value="Hard">Hard</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-1.5">
                  <Label>Topic (for custom mode)</Label>
                  <Input value={quizTopic} onChange={(e) => setQuizTopic(e.target.value)} placeholder="e.g. trigonometry" />
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-1.5">
                    <Label>Questions</Label>
                    <Input
                      type="number"
                      min={5}
                      max={40}
                      value={quizQuestionCount}
                      onChange={(e) => setQuizQuestionCount(Number(e.target.value || 15))}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Duration (sec)</Label>
                    <Input
                      type="number"
                      min={120}
                      max={3600}
                      value={quizDurationSeconds}
                      onChange={(e) => setQuizDurationSeconds(Number(e.target.value || 900))}
                    />
                  </div>
                </div>

                <Button onClick={() => void createQuizChallenge()} disabled={!allCommunityUsers.length}>Send Challenge</Button>
              </CardContent>
            </Card>

            <div className="min-w-0 space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle>Challenge Inbox & History</CardTitle>
                  <CardDescription>Your quiz challenges.</CardDescription>
                </CardHeader>
                <CardContent className="space-y-2 max-h-[280px] overflow-auto">
                  {quizChallenges.map((challenge) => {
                    const opponentUserId = challenge.isChallenger ? challenge.opponentUserId : challenge.challengerUserId;
                    const opponent = allCommunityUsers.find((row) => row.id === opponentUserId)
                      || connections.find((item) => item.user.id === opponentUserId)?.user;
                    return (
                      <button
                        key={challenge.id}
                        type="button"
                        onClick={() => setSelectedQuizChallengeId(challenge.id)}
                        className={`w-full rounded-lg border p-3 text-left ${selectedQuizChallengeId === challenge.id ? 'border-indigo-400 bg-indigo-50' : ''}`}
                      >
                        <div className="flex items-center justify-between gap-2">
                          <p className="text-sm font-medium">{challenge.mode} {challenge.subject ? ` ${challenge.subject}` : ''}</p>
                          <Badge variant="outline">{challenge.status}</Badge>
                        </div>
                        <p className="text-xs text-muted-foreground">
                          {challenge.isChallenger ? 'vs' : 'from'} {opponent ? displayName(opponent) : 'Student'}  {challenge.questionCount} Q  {Math.round(challenge.durationSeconds / 60)} min  {challenge.challengeType || 'async'}
                        </p>
                        {challenge.status === 'pending' && !challenge.isChallenger ? (
                          <div className="mt-2 flex gap-2">
                            <Button size="sm" onClick={(e) => { e.stopPropagation(); void respondQuizChallenge(challenge.id, 'accept'); }}>Accept</Button>
                            <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); void respondQuizChallenge(challenge.id, 'decline'); }}>Decline</Button>
                          </div>
                        ) : null}
                        {(challenge.status === 'in_progress' || challenge.status === 'accepted') && !challenge.myResult.submitted ? (
                          <Button
                            className="mt-2"
                            size="sm"
                            variant="secondary"
                            onClick={(e) => {
                              e.stopPropagation();
                              startChallengeAttempt(challenge.id);
                            }}
                          >
                            Start Attempt
                          </Button>
                        ) : null}
                      </button>
                    );
                  })}
                  {!quizChallenges.length ? <p className="text-xs text-muted-foreground">No quiz challenges yet.</p> : null}
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Battle Arena</CardTitle>
                  <CardDescription>
                    {selectedQuizChallenge
                      ? `${selectedQuizChallenge.questionCount} questions | ${Math.round(selectedQuizChallenge.durationSeconds / 60)} minutes | ${selectedQuizChallenge.challengeType || 'async'}`
                      : 'Select a challenge to play.'}
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  {selectedQuizChallenge ? (
                    <>
                      {(selectedQuizChallenge.status === 'in_progress' || selectedQuizChallenge.status === 'accepted') && !selectedQuizChallenge.myResult.submitted ? (
                        <>
                          <p className="text-xs text-muted-foreground">Challenge is active.</p>
                          {selectedQuizChallenge.challengeType === 'live' ? (
                            <div className="rounded-md border bg-slate-50 p-2 text-xs text-muted-foreground">
                              <p>Your progress: {selectedQuizChallenge.myLiveProgress?.answeredCount || 0}/{selectedQuizChallenge.questionCount}</p>
                              <p>Opponent progress: {selectedQuizChallenge.opponentLiveProgress?.answeredCount || 0}/{selectedQuizChallenge.questionCount}</p>
                            </div>
                          ) : null}
                          <Button onClick={() => startChallengeAttempt(selectedQuizChallenge.id)}>Open Challenge Test Interface</Button>
                        </>
                      ) : (
                        <div className="space-y-2 rounded-md border p-3 text-sm">
                          <p>Status: {selectedQuizChallenge.status}</p>
                          <p>Your score: {selectedQuizChallenge.myResult.totalScore.toFixed(2)} ({selectedQuizChallenge.myResult.correctCount}/{selectedQuizChallenge.questionCount} correct)</p>
                          <p>Opponent score: {selectedQuizChallenge.opponentResult.totalScore.toFixed(2)}</p>
                          {selectedQuizChallenge.status === 'completed' ? (
                            <p className="font-medium">
                              {selectedQuizChallenge.winnerUserId
                                ? (selectedQuizChallenge.winnerUserId === user.id ? 'You won this battle.' : 'You lost this battle.')
                                : 'Battle ended in a tie.'}
                            </p>
                          ) : null}
                        </div>
                      )}
                    </>
                  ) : (
                    <p className="text-sm text-muted-foreground">Select a challenge.</p>
                  )}
                </CardContent>
              </Card>

              <div className="grid gap-4 md:grid-cols-2">
                <Card>
                  <CardHeader>
                    <CardTitle>Your Quiz Profile</CardTitle>
                    <CardDescription>Your quiz results.</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-1 text-sm">
                    <p>Total wins: {quizLeaderboard.find((row) => row.userId === user.id)?.totalWins || 0}</p>
                    <p>Total matches: {quizLeaderboard.find((row) => row.userId === user.id)?.totalMatchesPlayed || 0}</p>
                    <p>Win rate: {quizLeaderboard.find((row) => row.userId === user.id)?.winRate || 0}%</p>
                    <p>Challenges sent: {quizLeaderboard.find((row) => row.userId === user.id)?.totalChallengesSent || 0}</p>
                    <p>Challenges accepted: {quizLeaderboard.find((row) => row.userId === user.id)?.totalChallengesAccepted || 0}</p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>Quiz Battles Leaderboard</CardTitle>
                    <CardDescription>Top performers.</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-2 max-h-[220px] overflow-auto" ref={quizLeaderboardScrollRef}>
                    <div className="relative w-full" style={{ height: quizLeaderboardVirtual.getTotalSize() }}>
                      {quizLeaderboardVirtual.getVirtualItems().map((vi) => {
                        const entry = quizLeaderboard[vi.index];
                        return (
                          <div
                            key={entry.userId}
                            className="absolute left-0 top-0 w-full px-0.5"
                            style={{ height: vi.size, transform: `translateY(${vi.start}px)` }}
                          >
                            <div className="rounded-md border p-2 text-sm">
                              <p className="font-medium">#{entry.rank} {entry.name || entry.username || 'Student'}</p>
                              <p className="text-xs text-muted-foreground">Wins {entry.totalWins} | Matches {entry.totalMatchesPlayed} | Win Rate {entry.winRate}%</p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    {!quizLeaderboard.length ? <p className="text-xs text-muted-foreground">No quiz ranking data yet.</p> : null}
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="leaderboard" className="mt-0 space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Competitive Leaderboard</CardTitle>
              <CardDescription>Weekly and monthly rankings.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-center gap-2">
                <Button size="sm" variant={leaderboardPeriod === 'weekly' ? 'default' : 'outline'} onClick={() => setLeaderboardPeriod('weekly')}>Weekly</Button>
                <Button size="sm" variant={leaderboardPeriod === 'monthly' ? 'default' : 'outline'} onClick={() => setLeaderboardPeriod('monthly')}>Monthly</Button>
              </div>
              <div className="max-h-[360px] overflow-auto" ref={competitiveLeaderboardScrollRef}>
                <div className="relative w-full" style={{ height: competitiveLeaderboardVirtual.getTotalSize() }}>
                  {competitiveLeaderboardVirtual.getVirtualItems().map((vi) => {
                    const entry = leaderboard[vi.index];
                    return (
                      <div
                        key={entry.id}
                        className="absolute left-0 top-0 w-full px-0.5"
                        style={{ height: vi.size, transform: `translateY(${vi.start}px)` }}
                      >
                        <div className="flex items-center justify-between rounded-lg border p-3">
                          <div>
                            <p className="text-sm">#{entry.rank} {displayName(entry)}</p>
                            <p className="text-xs text-muted-foreground">Avg {entry.averageScore}  Acc {entry.accuracy}%  Improvement {entry.improvement}</p>
                          </div>
                          <Badge variant="outline">{Math.round(Number(entry.score || 0))}</Badge>
                        </div>
                      </div>
                    );
                  })}
                </div>
                {!leaderboard.length ? <p className="text-xs text-muted-foreground">No ranking data yet.</p> : null}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Achievement Badges</CardTitle>
              <CardDescription>Your badges.</CardDescription>
            </CardHeader>
            <CardContent className="grid min-h-[120px] gap-2 md:grid-cols-2 xl:grid-cols-3">
              {badges.map((badge) => (
                <button
                  key={badge.id}
                  type="button"
                  disabled={!badge.earned}
                  onClick={() => {
                    if (badge.earned) setSelectedBadge(badge);
                  }}
                  className={`rounded-lg border p-3 text-left transition ${
                    badge.earned
                      ? 'border-emerald-300 bg-emerald-50/60 hover:border-emerald-400 hover:shadow-sm'
                      : 'cursor-default opacity-95'
                  }`}
                >
                  <p className="text-sm">
                    <span className="mr-1" aria-hidden="true">{badgeIcon(badge)}</span>
                    {badge.label}
                  </p>
                  <p className="text-xs text-muted-foreground">{badge.progress}/{badge.target}</p>
                  <Badge variant={badge.earned ? 'default' : 'outline'} className="mt-2">{badge.earned ? 'Unlocked' : 'Locked'}</Badge>
                </button>
              ))}
            </CardContent>
          </Card>

          <Dialog open={Boolean(selectedBadge)} onOpenChange={(open) => { if (!open) setSelectedBadge(null); }}>
            <DialogContent className="overflow-hidden sm:max-w-lg">
              {selectedBadge ? (
                <div className="net360-badge-celebrate relative">
                  <div className="net360-badge-confetti" aria-hidden="true">
                    <span /><span /><span /><span /><span /><span />
                  </div>
                  <DialogHeader>
                    <DialogTitle className="text-center text-xl">Congratulations</DialogTitle>
                    <DialogDescription className="text-center">
                      You unlocked this NET360 achievement.
                    </DialogDescription>
                  </DialogHeader>
                  <div className="net360-badge-pop rounded-2xl border border-indigo-200 bg-gradient-to-br from-indigo-50 via-white to-violet-50 p-5 text-center dark:border-indigo-500/40 dark:from-slate-900 dark:via-slate-950 dark:to-indigo-950">
                    <p className="text-5xl" aria-hidden="true">{badgeIcon(selectedBadge)}</p>
                    <p className="mt-3 text-lg font-semibold text-slate-900 dark:text-slate-50">{selectedBadge.label}</p>
                    <p className="mt-2 text-sm text-slate-600 dark:text-slate-300">
                      {selectedBadge.description || 'This badge is now part of your NET360 Community profile.'}
                    </p>
                    {formatBadgeUnlockDate(selectedBadge.unlockedAt) ? (
                      <p className="mt-3 text-xs font-medium text-indigo-700 dark:text-indigo-300">
                        Unlocked {formatBadgeUnlockDate(selectedBadge.unlockedAt)}
                      </p>
                    ) : null}
                    <p className="mt-2 text-xs text-muted-foreground">
                      Progress {selectedBadge.progress}/{selectedBadge.target}
                    </p>
                  </div>
                  <DialogFooter className="mt-2 sm:justify-center">
                    <Button
                      type="button"
                      onClick={() => void downloadBadgeCertificate(selectedBadge)}
                      disabled={isDownloadingCertificate}
                    >
                      {isDownloadingCertificate ? 'Preparing PDF…' : 'Download certificate'}
                    </Button>
                  </DialogFooter>
                </div>
              ) : null}
            </DialogContent>
          </Dialog>
        </TabsContent>

        <TabsContent value="messages" className="mt-0 space-y-4">
          <div className="grid gap-4 xl:grid-cols-3">
            <Card className="min-w-0 xl:col-span-1">
              <CardHeader>
                <CardTitle>Connected Students</CardTitle>
                <CardDescription>Your connections.</CardDescription>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[420px] pr-2 sm:h-[500px]">
                  <div className="space-y-2">
                    {connections.map((item) => (
                      <button
                        key={item.connectionId}
                        type="button"
                        onClick={() => setActiveConnectionId(item.connectionId)}
                        className={`w-full rounded-lg border p-3 text-left ${activeConnectionId === item.connectionId ? 'border-indigo-400 bg-indigo-50' : ''}`}
                      >
                        <div className="flex items-center gap-2">
                          <CommunityAvatar userLike={item.user} />
                          <div>
                            <p className="text-sm">{displayName(item.user)}</p>
                            <p className="text-xs text-muted-foreground">{item.user.targetProgram || 'Study partner'}</p>
                          </div>
                        </div>
                        {item.unreadCount > 0 ? <Badge className="mt-2">{item.unreadCount} unread</Badge> : null}
                      </button>
                    ))}
                    {!connections.length ? <p className="text-xs text-muted-foreground">No active connections yet.</p> : null}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>

            <Card className="min-w-0 xl:col-span-2">
              <CardHeader>
                <CardTitle>Private Chat</CardTitle>
                <CardDescription>
                  {activeConnection ? (
                    <span className="inline-flex flex-wrap items-center gap-2">
                      <CommunityAvatar userLike={activeConnection.user} sizeClass="h-6 w-6" />
                      <span>{`Chat with ${displayName(activeConnection.user)}`}</span>
                      {peerChatPresence ? (
                        <span className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[11px] font-medium text-emerald-800 dark:border-emerald-500/40 dark:bg-emerald-950/40 dark:text-emerald-100">
                          <span className={`h-1.5 w-1.5 rounded-full ${peerChatPresence.presenceStatus === 'away' ? 'bg-amber-400' : 'bg-emerald-500'} animate-pulse shadow-[0_0_6px_currentColor]`} />
                          {peerChatPresence.presenceStatus === 'away' ? 'Away' : 'Online'}
                        </span>
                      ) : (
                        <span className="text-xs text-muted-foreground">Status hidden or offline</span>
                      )}
                    </span>
                  ) : 'Select a conversation.'}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-center justify-between rounded-md border border-emerald-200 bg-emerald-50/70 px-3 py-2 text-xs text-emerald-800 dark:border-emerald-500/45 dark:bg-emerald-950/40 dark:text-emerald-100">
                  <span className="text-inherit">{ENCRYPTION_LABEL}</span>
                  {activeConnection ? (
                    <div className="flex items-center gap-2">
                      {activeConnection.blockedByOther ? <Badge variant="outline">Blocked by user</Badge> : null}
                      {activeConnection.blockedByMe ? <Badge variant="outline">You blocked this user</Badge> : null}
                    </div>
                  ) : null}
                </div>

                {activeConnection ? (
                  <div className="flex flex-wrap gap-2">
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => messageFileInputRef.current?.click()}
                      disabled={activeConnection.canMessage === false || isSendingMessage}
                    >
                      Attach File
                    </Button>
                    <span className="self-center text-[11px] text-muted-foreground">PDF or JPG/PNG/WEBP, up to 10MB</span>
                    <Button type="button" size="sm" variant="outline" onClick={() => void toggleBlockConnection()} disabled={isBlockingConnection || isSendingMessage}>
                      {isBlockingConnection ? 'Updating...' : activeConnection.blockedByMe ? 'Unblock' : 'Block'}
                    </Button>
                    <Button type="button" size="sm" variant="outline" onClick={() => void unfriendConnection()} disabled={isUnfriendingConnection || isSendingMessage}>
                      {isUnfriendingConnection ? 'Removing...' : 'Unfriend'}
                    </Button>
                    <input
                      ref={messageFileInputRef}
                      type="file"
                      accept={CHAT_ATTACHMENT_ACCEPT}
                      className="hidden"
                      onChange={(e) => void onMessageFileSelected(e)}
                    />
                  </div>
                ) : null}

                {messageAttachment ? (
                  <div className="rounded-md border bg-slate-50 px-3 py-2 text-xs">
                    <p className="font-medium">Ready to send: {messageAttachment.name}</p>
                    <p className="text-muted-foreground">{Math.max(1, Math.round(messageAttachment.size / 1024))} KB</p>
                    <div className="mt-2 flex gap-2">
                      <Button type="button" size="sm" onClick={() => void sendFileMessage()} disabled={!activeConnection || activeConnection.canMessage === false || isSendingMessage}>Send File</Button>
                      <Button type="button" size="sm" variant="outline" onClick={() => setMessageAttachment(null)}>Remove</Button>
                    </div>
                  </div>
                ) : null}

                <div className="max-h-[300px] overflow-auto rounded-xl border border-slate-200/90 bg-gradient-to-b from-slate-50/50 to-white p-3 space-y-2 shadow-inner dark:border-slate-700 dark:from-slate-900/40 dark:to-slate-950/20 sm:max-h-[380px]">
                  {messagesThreadLoading ? (
                    <div className="space-y-2">
                      <Skeleton className="h-10 w-[72%] rounded-lg" />
                      <Skeleton className="ml-auto h-10 w-[64%] rounded-lg" />
                      <Skeleton className="h-10 w-[80%] rounded-lg" />
                    </div>
                  ) : (
                    <>
                      {messages.map((item) => {
                        const isMine = item.senderUserId === user.id;
                        const peerHasRead = isMine && activeConnection
                          ? (item.readByUserIds || []).includes(activeConnection.user.id)
                          : false;
                        return (
                          <div
                            key={item.id}
                            className={`max-w-[88%] rounded-2xl p-2.5 text-sm shadow-sm transition-all duration-200 ${
                              isMine
                                ? 'chat-bubble-own ml-auto bg-gradient-to-br from-indigo-600 to-violet-600 text-white'
                                : 'bg-white text-slate-800 ring-1 ring-slate-200 dark:bg-slate-800/90 dark:text-slate-100 dark:ring-slate-600'
                            }`}
                          >
                      {item.messageType === 'call-invite' && item.callInvite?.roomUrl ? (
                        <div className="space-y-1">
                          <p>{item.text || `${item.callInvite.mode === 'video' ? 'Video' : 'Audio'} call invite`}</p>
                          <a href={item.callInvite.roomUrl} target="_blank" rel="noreferrer" className="text-xs underline underline-offset-2">
                            Join {item.callInvite.mode === 'video' ? 'Video' : 'Audio'} Call
                          </a>
                        </div>
                      ) : null}
                      {item.messageType === 'file' && item.attachment ? (
                        <SafeChatAttachment
                          messageId={item.id}
                          attachment={item.attachment}
                          token={token}
                        />
                      ) : null}
                      {item.messageType === 'voice' && item.attachment ? (
                        <div className="space-y-1">
                          <p>{item.text || `Voice note (${item.voiceMeta?.durationSeconds || 0}s)`}</p>
                          <audio controls src={item.attachment.dataUrl} className="w-full" />
                        </div>
                      ) : null}
                      {(!item.messageType || item.messageType === 'text') && item.text ? <p>{item.text}</p> : null}
                      <div className="mt-1 flex flex-wrap gap-1">
                        {QUICK_CHAT_EMOJIS.map((emoji) => (
                          <button
                            key={`${item.id}-${emoji}`}
                            type="button"
                            className={`rounded border px-1.5 py-0.5 text-[11px] ${
                              isMine
                                ? 'border-white/25 bg-white/15 text-white hover:bg-white/25'
                                : 'bg-white/70 text-slate-800 dark:border-slate-500 dark:bg-slate-700 dark:text-slate-100'
                            }`}
                            onClick={() => void toggleMessageReaction(item.id, emoji)}
                          >
                            {emoji}
                          </button>
                        ))}
                      </div>
                      {Array.isArray(item.reactions) && item.reactions.length ? (
                        <p className={`mt-1 text-[11px] ${isMine ? 'text-indigo-100/90' : 'text-muted-foreground'}`}>
                          {item.reactions.map((reaction) => reaction.emoji).join(' ')}
                        </p>
                      ) : null}
                      <div className={`mt-1 flex flex-wrap items-center justify-between gap-2 text-[11px] ${isMine ? 'text-indigo-100/85' : 'text-muted-foreground'}`}>
                        <span>{item.createdAt ? new Date(item.createdAt).toLocaleString() : ''}</span>
                        {isMine ? <span className="font-medium">{peerHasRead ? 'Seen' : 'Sent'}</span> : null}
                      </div>
                    </div>
                        );
                      })}
                      {remoteTyping ? (
                        <div className="rounded-lg border border-dashed border-slate-200 bg-white/70 px-2 py-1.5 text-xs text-muted-foreground dark:border-slate-600 dark:bg-slate-800/60">
                          <span className="inline-flex items-center gap-2">
                            <span className="inline-flex gap-0.5">
                              <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-indigo-400 [animation-duration:0.6s]" />
                              <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-indigo-400 [animation-duration:0.6s] [animation-delay:120ms]" />
                              <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-indigo-400 [animation-duration:0.6s] [animation-delay:240ms]" />
                            </span>
                            {activeConnection ? `${displayName(activeConnection.user)} is typing…` : 'Typing…'}
                          </span>
                        </div>
                      ) : null}
                      <div ref={messageThreadEndRef} />
                      {!messages.length ? <p className="text-xs text-muted-foreground">No messages yet.</p> : null}
                    </>
                  )}
                </div>
                <form
                  className="flex flex-col gap-2 sm:flex-row"
                  onSubmit={(event) => {
                    event.preventDefault();
                    void sendMessage();
                  }}
                >
                  <Input
                    value={messageInput}
                    onChange={(e) => {
                      setMessageInput(e.target.value);
                      if (activeConnection && activeConnection.canMessage !== false && activeConnectionId) {
                        bumpTypingFromInput();
                      }
                    }}
                    placeholder={activeConnection ? 'Type a message...' : 'Select a conversation'}
                    disabled={!activeConnection || isSendingMessage || activeConnection.canMessage === false}
                  />
                  <Button type="submit" className="w-full sm:w-auto" disabled={!activeConnection || isSendingMessage || !messageInput.trim() || activeConnection.canMessage === false}>
                    {isSendingMessage ? 'Sending...' : 'Send'}
                  </Button>
                </form>
                {activeConnection ? (
                  <div className="space-y-2 rounded-lg border border-amber-200 bg-amber-50/60 p-3">
                    <Label>Report this conversation</Label>
                    <Textarea
                      value={reportReason}
                      onChange={(e) => setReportReason(e.target.value)}
                      className="min-h-[80px]"
                      placeholder="Describe what happened."
                    />
                    <Button variant="outline" onClick={() => void reportConversation()} disabled={!reportReason.trim()}>
                      Submit report
                    </Button>
                  </div>
                ) : null}
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

export const Community = memo(CommunityInner);
